import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, Button, Spinner, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { Tabs } from "@/components/ui/Tabs";
import { InvoicePnlDrill } from "@/components/InvoicePnlDrill";
import { TrendingUp, TrendingDown, Minus, Printer, Target, Package, AlertTriangle, Repeat, DollarSign, Boxes, Layers, FileText, Users, X, Search, CheckCircle2 } from "lucide-react";

function periodDefault() {
  // Data inicial fixa: 01/07/2026 (início da operação atual)
  const end = new Date();
  return { from: "2026-07-01", to: end.toISOString().slice(0, 10) };
}

export default function Reports() {
  const [sp, setSp] = useSearchParams();
  const initialTab = sp.get("tab") || "kpis";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = sp.get("tab");
    if (t && t !== tab) setTab(t);
  }, [sp, tab]);
  const changeTab = (t) => {
    setTab(t);
    setSp({ tab: t }, { replace: true });
  };
  const [{ from, to }, setRange] = useState(periodDefault());
  return (
    <Layout
      title="Relatórios"
      subtitle="KPIs, DRE, P&L e fechamento semanal"
      actions={
        <div className="flex items-center gap-2 text-sm print:hidden">
          <input type="date" value={from} onChange={(e) => setRange({ from: e.target.value, to })} className="rounded-md bg-white border border-[#D6D2CC] px-3 py-2" data-testid="report-from" />
          <span className="text-ink-500">até</span>
          <input type="date" value={to} onChange={(e) => setRange({ from, to: e.target.value })} className="rounded-md bg-white border border-[#D6D2CC] px-3 py-2" data-testid="report-to" />
          <button
            onClick={() => window.print()}
            className="ml-2 inline-flex items-center gap-1.5 rounded-md bg-brand-500 hover:bg-brand-600 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
            data-testid="report-print-btn"
          >
            <Printer size={14} /> Imprimir
          </button>
        </div>
      }
    >
      <div className="print:hidden">
        <Tabs
          value={tab}
          onChange={changeTab}
          tabs={[
            { value: "kpis", label: "KPIs" },
            { value: "dre", label: "DRE" },
            { value: "by-unidade", label: "P&L por Unidade" },
            { value: "by-vendedor", label: "P&L por Vendedor" },
            { value: "by-invoice", label: "P&L por NF" },
            { value: "by-shipment", label: "P&L por Nota de Venda" },
            { value: "by-client", label: "P&L por Cliente" },
            { value: "by-supplier", label: "P&L por Fornecedor" },
            { value: "producer-statement", label: "Extrato do Produtor" },
            { value: "producer-target", label: "Preço Alvo do Produtor" },
            { value: "by-category", label: "Contas por Categoria" },
            { value: "custo-por-saco", label: "Custo por Saco" },
            { value: "sacos-maquinados", label: "Sacos Maquinados" },
            { value: "saidas-por-unidade", label: "Saídas por Unidade" },
            { value: "weekly", label: "Fechamento Semanal" },
            { value: "terceiros", label: "Terceiros" },
          ]}
        />
      </div>
      <div className="mt-6">
        {tab === "kpis" && <KpisView from={from} to={to} />}
        {tab === "dre" && <DreView from={from} to={to} />}
        {tab === "by-unidade" && <UnidadeView from={from} to={to} />}
        {tab === "by-vendedor" && <VendedorView from={from} to={to} />}
        {tab === "by-invoice" && <PnlTable url="/reports/pnl/by-invoice" from={from} to={to} kind="invoice" />}
        {tab === "by-shipment" && <PnlByShipmentView from={from} to={to} />}
        {tab === "by-client" && <PnlTable url="/reports/pnl/by-client" from={from} to={to} kind="client" />}
        {tab === "by-supplier" && <PnlTable url="/reports/pnl/by-supplier" from={from} to={to} kind="supplier" />}
        {tab === "producer-statement" && <ProducerStatementView from={from} to={to} />}
        {tab === "producer-target" && <ProducerTargetPriceView from={from} to={to} />}
        {tab === "by-category" && <ByCategoryView from={from} to={to} />}
        {tab === "custo-por-saco" && <CustoPorSacoView from={from} to={to} />}
        {tab === "sacos-maquinados" && <SacosMaquinadosView from={from} to={to} />}
        {tab === "saidas-por-unidade" && <SaidasPorUnidadeView from={from} to={to} />}
        {tab === "weekly" && <WeeklyView />}
        {tab === "terceiros" && <TerceirosReport from={from} to={to} />}
      </div>
    </Layout>
  );
}

function KpiCard({ icon: Icon, label, value, hint, tone = "default" }) {
  const tones = {
    default: "text-ink-900",
    ok: "text-ok",
    warn: "text-warn",
    danger: "text-danger",
  };
  const iconTones = {
    default: "bg-stone-100 text-ink-500",
    ok: "bg-emerald-100 text-ok",
    warn: "bg-amber-100 text-warn",
    danger: "bg-red-100 text-danger",
  };
  return (
    <Card className="p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconTones[tone]}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-500 truncate">{label}</div>
          <div className={`font-mono text-xl font-semibold mt-0.5 truncate ${tones[tone]}`}>{value}</div>
          {hint && <div className="text-[11px] text-ink-500 mt-0.5 truncate">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}

function KpisView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get("/reports/kpis", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  const k = data?.kpis || {};
  const c = data?.custo_por_saco || {};

  const custoData = [
    { name: "Mercadoria", value: c.mercadoria || 0, fill: "#1E3F33" },
    { name: "Frete entrada", value: c.frete_entrada || 0, fill: "#4A7C69" },
    { name: "Descarga/Chapa", value: c.descarga || 0, fill: "#C9A24E" },
    { name: "Maquinação", value: c.maquinacao || 0, fill: "#8B5E3C" },
    { name: "Sacaria", value: c.sacaria || 0, fill: "#A8C4B8" },
  ].filter((d) => d.value > 0);

  const margemTone = k.margem_bruta_pct >= 10 ? "ok" : k.margem_bruta_pct >= 0 ? "warn" : "danger";
  const inadTone = k.inadimplencia_pct <= 5 ? "ok" : k.inadimplencia_pct <= 15 ? "warn" : "danger";
  const giroTone = k.giro_estoque >= 2 ? "ok" : k.giro_estoque >= 1 ? "warn" : "danger";

  return (
    <div data-testid="kpis-view">
      {/* Hint quando há saídas não finalizadas */}
      {(k.n_rascunho > 0 || k.n_pendente > 0) && (
        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-3" data-testid="kpis-pending-hint">
          <AlertTriangle size={18} className="text-warn flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">
              {k.n_rascunho + k.n_pendente} saída(s) ainda não finalizada(s) — receita prevista de <span className="font-mono">{fmtBRL(k.receita_prevista)}</span>
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Só saídas <strong>finalizadas</strong> entram no cálculo de receita, lucro e margem. Finalize suas saídas para vê-las aqui.
            </div>
            <a href="/shipments" className="inline-block mt-1.5 text-xs font-semibold text-warn hover:underline">Ir para Saídas →</a>
          </div>
        </div>
      )}

      {/* Top row: Financial performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard icon={DollarSign} label="Receita líquida" value={fmtBRL(k.receita)} hint={`${k.n_shipments || 0} romaneios · bruto ${fmtBRL(k.receita_bruta)}`} />
        <KpiCard icon={TrendingDown} label="CMV" value={fmtBRL(k.cmv)} hint="Custo da mercadoria" />
        <KpiCard icon={TrendingUp} label="Lucro bruto" value={fmtBRL(k.lucro_bruto)} tone={margemTone} hint={`margem ${k.margem_bruta_pct || 0}%`} />
        <KpiCard icon={Target} label="Margem bruta" value={`${k.margem_bruta_pct || 0}%`} tone={margemTone} hint="Meta: >10%" />
      </div>

      {/* Second row: Operational KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Package} label="Ticket médio" value={fmtBRL(k.ticket_medio)} hint="Receita / romaneios" />
        <KpiCard icon={Package} label="Lucro por saco" value={fmtBRL(k.lucro_por_saco)} tone={k.lucro_por_saco > 0 ? "ok" : "danger"} hint={`${fmtNum(k.caixas_vendidas, 0)} caixas vendidas`} />
        <KpiCard icon={AlertTriangle} label="Inadimplência" value={`${k.inadimplencia_pct || 0}%`} tone={inadTone} hint={`${fmtBRL(k.ar_vencido)} vencidos / ${fmtBRL(k.ar_aberto)} aberto`} />
        <KpiCard icon={Repeat} label="Giro de estoque" value={`${k.giro_estoque || 0}x`} tone={giroTone} hint={`Vendas / ${fmtNum(k.estoque_atual_caixas, 0)} cx em estoque`} />
      </div>

      {/* Cost structure card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="mb-4">
            <h3 className="font-heading text-lg font-bold text-ink-900">Estrutura de custos por saco</h3>
            <p className="text-xs text-ink-500">Baseado em {fmtNum(c.sacos_beneficiados || 0, 0)} sacos beneficiados</p>
          </div>
          <table className="w-full text-sm">
            <tbody>
              <CostRow label="Mercadoria (compra)" value={c.mercadoria} />
              <CostRow label="Frete de entrada" value={c.frete_entrada} />
              <CostRow label="Chapa/Descarga" value={c.descarga} />
              <CostRow label="Maquinação" value={c.maquinacao} />
              <CostRow label="Sacaria" value={c.sacaria} />
              <tr className="border-t-2 border-ink-900">
                <td className="pl-2 py-3 font-heading font-extrabold uppercase tracking-wider text-sm text-ink-900">Custo total por saco</td>
                <td className="pr-2 py-3 text-right font-mono text-xl font-bold text-brand-500" data-testid="custo-total-saco">{fmtBRL(c.total)}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        <Card className="p-6">
          <div className="mb-2">
            <h3 className="font-heading text-lg font-bold text-ink-900">Composição do custo</h3>
            <p className="text-xs text-ink-500">Distribuição percentual</p>
          </div>
          <div className="h-72 min-h-[280px]">
            {custoData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-ink-500">Sem dados de custo no período.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={custoData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={90} paddingAngle={2}>
                    {custoData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E2DC", fontSize: 12 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function CostRow({ label, value }) {
  return (
    <tr className="border-b border-tertiary last:border-0">
      <td className="pl-2 py-2 text-ink-700">{label}</td>
      <td className="pr-2 py-2 text-right font-mono text-ink-900">{fmtBRL(value || 0)}</td>
    </tr>
  );
}

function VendedorView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get("/reports/pnl/by-vendedor", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const chartData = rows.map((r) => ({ vendedor: r.vendedor, Receita: r.receita, Lucro: r.lucro, Comissão: r.comissao }));

  return (
    <div data-testid="vendedor-view">
      {rows.length === 0 ? (
        <Card className="p-10 text-center">
          <div className="text-sm text-ink-500">Nenhuma saída finalizada no período — sem dados por vendedor.</div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {rows.map((r) => {
              const positive = r.lucro >= 0;
              return (
                <Card key={r.vendedor} className="p-5" data-testid={`vendedor-card-${r.vendedor}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-bold text-ink-900">{r.vendedor}</h3>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${positive ? "bg-emerald-100 text-ok" : "bg-red-100 text-danger"}`}>
                      {positive ? "lucro" : "prejuízo"}
                    </span>
                  </div>
                  <div className={`font-mono text-2xl font-bold ${positive ? "text-ok" : "text-danger"}`}>{fmtBRL(r.lucro)}</div>
                  <div className="text-xs text-ink-500 mt-0.5">margem {r.margem_pct}%</div>
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-tertiary text-xs">
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Vendas</div>
                      <div className="font-mono text-sm text-ink-900">{fmtBRL(r.receita)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Comissão</div>
                      <div className="font-mono text-sm text-warn font-semibold">{fmtBRL(r.comissao)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Saídas</div>
                      <div className="font-mono text-sm text-ink-900">{r.n_saidas}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Ticket médio</div>
                      <div className="font-mono text-sm text-ink-900">{fmtBRL(r.ticket_medio)}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="p-6 mb-6">
            <h3 className="font-heading font-bold text-ink-900 mb-4">Comparativo entre vendedores</h3>
            <div className="h-72 min-h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                  <XAxis dataKey="vendedor" tick={{ fontSize: 12 }} stroke="#57534E" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#57534E" />
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E2DC", fontSize: 12 }} />
                  <Legend iconType="rect" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Receita" fill="#4A7C69" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Lucro" fill="#1E3F33" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Comissão" fill="#C9A24E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3">Vendedor</th>
                  <th className="px-4 py-3 text-right">Saídas</th>
                  <th className="px-4 py-3 text-right">Caixas</th>
                  <th className="px-4 py-3 text-right">Ticket médio</th>
                  <th className="px-4 py-3 text-right">Receita bruta</th>
                  <th className="px-4 py-3 text-right">Receita líq.</th>
                  <th className="px-4 py-3 text-right">CMV</th>
                  <th className="px-4 py-3 text-right">Lucro</th>
                  <th className="px-4 py-3 text-right">Margem</th>
                  <th className="px-4 py-3 text-right">Comissão</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.vendedor} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-ink-900">{r.vendedor}</td>
                    <td className="px-4 py-3 font-mono text-right">{r.n_saidas}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtNum(r.caixas, 0)}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.ticket_medio)}</td>
                    <td className="px-4 py-3 font-mono text-right text-ink-500">{fmtBRL(r.receita_bruta)}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.receita)}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.cmv)}</td>
                    <td className={`px-4 py-3 font-mono text-right font-semibold ${r.lucro >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(r.lucro)}</td>
                    <td className={`px-4 py-3 font-mono text-right ${r.margem_pct >= 10 ? "text-ok" : r.margem_pct >= 0 ? "text-warn" : "text-danger"}`}>{r.margem_pct}%</td>
                    <td className="px-4 py-3 font-mono text-right text-warn font-semibold">{fmtBRL(r.comissao)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-ink-900 bg-stone-50 font-bold">
                  <td className="px-4 py-3 text-ink-900" colSpan={5}>TOTAL</td>
                  <td className="px-4 py-3 font-mono text-right text-ink-900">{fmtBRL(totals.receita || 0)}</td>
                  <td className="px-4 py-3 font-mono text-right text-ink-900">{fmtBRL(totals.cmv || 0)}</td>
                  <td className={`px-4 py-3 font-mono text-right ${(totals.lucro || 0) >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(totals.lucro || 0)}</td>
                  <td></td>
                  <td className="px-4 py-3 font-mono text-right text-warn">{fmtBRL(totals.comissao || 0)}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function UnidadeView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get("/reports/pnl/by-unidade", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const chartData = rows.map((r) => ({
    unidade: r.unidade,
    Receita: r.receita,
    CMV: r.cmv,
    Lucro: r.lucro,
  }));

  return (
    <div data-testid="unidade-view">
      {rows.length === 0 ? (
        <Card className="p-10 text-center">
          <div className="text-sm text-ink-500">Nenhuma saída finalizada no período — sem dados por unidade.</div>
        </Card>
      ) : (
        <>
          {/* Cards por unidade */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {rows.map((r) => {
              const positive = r.lucro >= 0;
              return (
                <Card key={r.unidade} className="p-5" data-testid={`unidade-card-${r.unidade}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-bold text-ink-900">{r.unidade}</h3>
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${positive ? "bg-emerald-100 text-ok" : "bg-red-100 text-danger"}`}>
                      {positive ? "lucro" : "prejuízo"}
                    </span>
                  </div>
                  <div className={`font-mono text-3xl font-bold ${positive ? "text-ok" : "text-danger"}`}>
                    {fmtBRL(r.lucro)}
                  </div>
                  <div className="text-xs text-ink-500 mt-1">margem {r.margem_pct}%</div>
                  <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-tertiary text-xs">
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Receita líq.</div>
                      <div className="font-mono text-sm font-semibold text-ink-900">{fmtBRL(r.receita)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">CMV</div>
                      <div className="font-mono text-sm text-ink-900">{fmtBRL(r.cmv)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Sacos benef.</div>
                      <div className="font-mono text-sm font-semibold text-brand-500" data-testid={`unidade-sacos-benef-${r.unidade}`}>{fmtNum(r.sacos_beneficiados || 0, 0)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">Cx vendidas</div>
                      <div className="font-mono text-sm text-ink-900">{fmtNum(r.caixas, 0)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500 uppercase font-semibold text-[10px]">NFs / Saídas</div>
                      <div className="font-mono text-sm text-ink-900">{r.n_nfs} / {r.n_shipments}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Gráfico comparativo */}
          <Card className="p-6 mb-6">
            <h3 className="font-heading font-bold text-ink-900 mb-4">Comparativo entre unidades</h3>
            <div className="h-72 min-h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                  <XAxis dataKey="unidade" tick={{ fontSize: 12 }} stroke="#57534E" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#57534E" />
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E2DC", fontSize: 12 }} />
                  <Legend iconType="rect" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Receita" fill="#4A7C69" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="CMV" fill="#8B5E3C" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Lucro" fill="#1E3F33" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Tabela detalhada */}
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3">Unidade</th>
                  <th className="px-4 py-3 text-right">NFs</th>
                  <th className="px-4 py-3 text-right">Saídas</th>
                  <th className="px-4 py-3 text-right">Sacos benef.</th>
                  <th className="px-4 py-3 text-right">Cx vendidas</th>
                  <th className="px-4 py-3 text-right">Receita bruta</th>
                  <th className="px-4 py-3 text-right">Receita líq.</th>
                  <th className="px-4 py-3 text-right">CMV</th>
                  <th className="px-4 py-3 text-right">Lucro</th>
                  <th className="px-4 py-3 text-right">Margem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.unidade} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-ink-900">{r.unidade}</td>
                    <td className="px-4 py-3 font-mono text-right">{r.n_nfs}</td>
                    <td className="px-4 py-3 font-mono text-right">{r.n_shipments}</td>
                    <td className="px-4 py-3 font-mono text-right font-semibold text-brand-500">{fmtNum(r.sacos_beneficiados || 0, 0)}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtNum(r.caixas, 0)}</td>
                    <td className="px-4 py-3 font-mono text-right text-ink-500">{fmtBRL(r.receita_bruta)}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.receita)}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.cmv)}</td>
                    <td className={`px-4 py-3 font-mono text-right font-semibold ${r.lucro >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(r.lucro)}</td>
                    <td className={`px-4 py-3 font-mono text-right ${r.margem_pct >= 10 ? "text-ok" : r.margem_pct >= 0 ? "text-warn" : "text-danger"}`}>{r.margem_pct}%</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-ink-900 bg-stone-50 font-bold">
                  <td className="px-4 py-3 text-ink-900" colSpan={3}>TOTAL</td>
                  <td className="px-4 py-3 font-mono text-right text-brand-500" data-testid="unidade-total-sacos-benef">{fmtNum(totals.sacos_beneficiados || 0, 0)}</td>
                  <td className="px-4 py-3 font-mono text-right text-ink-900">{fmtNum(totals.caixas || 0, 0)}</td>
                  <td></td>
                  <td className="px-4 py-3 font-mono text-right text-ink-900">{fmtBRL(totals.receita || 0)}</td>
                  <td className="px-4 py-3 font-mono text-right text-ink-900">{fmtBRL(totals.cmv || 0)}</td>
                  <td className={`px-4 py-3 font-mono text-right ${(totals.lucro || 0) >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(totals.lucro || 0)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function DreView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get("/reports/dre", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  const s = data?.summary || {};

  const tone = (v) => v >= 0 ? "text-ok" : "text-danger";
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Receita Bruta</div><div className="font-mono text-2xl font-semibold text-ink-900 mt-1.5" data-testid="dre-receita-bruta">{fmtBRL(s.receita_bruta)}</div><div className="text-xs text-ink-500 mt-1">{data?.n_shipments || 0} saídas</div></Card>
        <Card className="p-5"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Lucro Bruto</div><div className={`font-mono text-2xl font-semibold mt-1.5 ${tone(s.lucro_bruto)}`}>{fmtBRL(s.lucro_bruto)}</div><div className="text-xs text-ink-500 mt-1">margem {s.margem_bruta_pct || 0}%</div></Card>
        <Card className="p-5"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">EBITDA</div><div className={`font-mono text-2xl font-semibold mt-1.5 ${tone(s.ebitda)}`}>{fmtBRL(s.ebitda)}</div></Card>
        <div className="p-5 rounded-lg shadow-sm border bg-brand-500 border-brand-500"><div className="text-xs font-bold uppercase tracking-[0.1em] text-emerald-100">Lucro Líquido</div><div className="font-mono text-2xl font-semibold text-white mt-1.5" data-testid="dre-lucro-liquido">{fmtBRL(s.lucro_liquido)}</div><div className="text-xs text-emerald-100 mt-1">margem {s.margem_liquida_pct || 0}%</div></div>
      </div>

      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-tertiary">
          <h3 className="font-heading text-lg font-bold text-ink-900">Demonstrativo do Resultado</h3>
          <p className="text-xs text-ink-500">Período: {from} → {to}</p>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {(data?.linhas || []).map((l, i) => {
              const isSubtotal = l.tipo === "subtotal";
              const isTotal = l.tipo === "total";
              const isCmv = l.label.includes("CMV");
              return (
                <tr key={i} className={`border-b border-tertiary ${isSubtotal ? "bg-stone-50 font-semibold" : ""} ${isTotal ? "bg-brand-500 text-white font-bold" : ""}`}>
                  <td className="px-6 py-3">
                    {l.label}
                    {isCmv && s.cmv_inclui && (
                      <span className="ml-2 text-[10px] text-ink-500 font-normal" title={`Incluído no CMV: ${s.cmv_inclui.join(", ")}`}>
                        ℹ inclui: {s.cmv_inclui.join(" · ")}
                      </span>
                    )}
                  </td>
                  <td className={`px-6 py-3 text-right font-mono ${l.tipo === "debito" && !isTotal ? "text-danger" : ""}`}>{fmtBRL(l.valor)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-6 py-3 text-xs text-ink-500 bg-stone-50 border-t border-tertiary">
          <b>Sobre a estrutura:</b> as despesas listadas depois do Lucro Bruto (frete CIF, descarga, seguro, comissões, aluguel, energia, impostos, etc.) <b>não</b> se repetem no CMV. Pagamentos de <b>fornecedor, frete de compra, folha (maquinação) e sacaria</b> lançados em Contas a Pagar já estão embutidos no CMV via os custos operacionais da NF beneficiada — por isso não aparecem duplicados aqui.
        </div>
      </Card>
    </>
  );
}

function PnlTable({ url, from, to, kind }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [drillId, setDrillId] = useState(null);
  const [fornFilter, setFornFilter] = useState("");
  const [sortBy, setSortBy] = useState("lucro"); // lucro | data | margem | receita
  useEffect(() => {
    setLoading(true);
    api.get(url, { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [url, from, to]);

  // Lista de fornecedores (para filtro) e linhas filtradas/ordenadas
  const rows = data?.rows || [];
  const fornecedores = useMemo(() => {
    if (kind !== "invoice") return [];
    const set = new Set();
    rows.forEach((r) => { if (r.fornecedor) set.add(r.fornecedor); });
    return Array.from(set).sort();
  }, [rows, kind]);
  const filteredRows = useMemo(() => {
    let out = rows;
    if (kind === "invoice" && fornFilter) {
      out = out.filter((r) => (r.fornecedor || "") === fornFilter);
    }
    if (kind === "invoice") {
      const cmp = {
        lucro: (a, b) => b.lucro - a.lucro,
        data: (a, b) => (b.data || "").localeCompare(a.data || ""),
        margem: (a, b) => (b.margem_pct || 0) - (a.margem_pct || 0),
        receita: (a, b) => b.receita - a.receita,
      };
      out = [...out].sort(cmp[sortBy] || cmp.lucro);
    }
    return out;
  }, [rows, kind, fornFilter, sortBy]);

  // Totais recalculados com base no filtro
  const filteredTotals = useMemo(() => {
    if (kind !== "invoice") return data?.totals || null;
    return {
      receita: filteredRows.reduce((a, r) => a + (r.receita || 0), 0),
      cmv: filteredRows.reduce((a, r) => a + (r.cmv_alocado || 0), 0),
      lucro: filteredRows.reduce((a, r) => a + (r.lucro || 0), 0),
    };
  }, [filteredRows, kind, data]);

  const chartData = useMemo(() => {
    const rowsChart = filteredRows.slice(0, 10);
    return rowsChart.map((r) => ({
      label: kind === "invoice" ? `NF ${r.nota_numero}` : kind === "client" ? r.cliente : r.fornecedor,
      lucro: r.lucro,
      receita: r.receita,
    }));
  }, [filteredRows, kind]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  const incompletas = kind === "invoice" ? filteredRows.filter((r) => r.custo_incompleto) : [];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Receita</div><div className="font-mono text-2xl font-semibold text-ink-900 mt-1.5">{fmtBRL(filteredTotals?.receita || 0)}</div></Card>
        <Card className="p-5"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Lucro</div><div className={`font-mono text-2xl font-semibold mt-1.5 ${(filteredTotals?.lucro || 0) >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(filteredTotals?.lucro || 0)}</div></Card>
        <Card className="p-5"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Linhas</div><div className="font-mono text-2xl font-semibold text-ink-900 mt-1.5">{filteredRows.length}{fornFilter && <span className="text-xs text-ink-500 ml-2">de {rows.length}</span>}</div></Card>
      </div>

      {/* Filtros e ordenação (só na aba P&L por NF) */}
      {kind === "invoice" && rows.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-1">Fornecedor / Produtor</label>
            <select
              value={fornFilter}
              onChange={(e) => setFornFilter(e.target.value)}
              className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm min-w-[220px]"
              data-testid="pnl-forn-filter"
            >
              <option value="">Todos ({fornecedores.length})</option>
              {fornecedores.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-1">Ordenar por</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
              data-testid="pnl-sort-by"
            >
              <option value="lucro">Maior lucro</option>
              <option value="data">Data (mais recente)</option>
              <option value="margem">Maior margem</option>
              <option value="receita">Maior receita</option>
            </select>
          </div>
          {fornFilter && (
            <button
              onClick={() => setFornFilter("")}
              className="text-xs text-brand-600 hover:underline mb-2"
            >
              Limpar filtro
            </button>
          )}
        </div>
      )}

      {incompletas.length > 0 && (
        <div
          className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-3"
          data-testid="pnl-custo-incompleto-banner"
        >
          <AlertTriangle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <div className="font-bold">
              {incompletas.length} NF{incompletas.length > 1 ? "s" : ""} sem custo lançado —
              o lucro/margem aparece como 100% porque não há valor de mercadoria ou custos operacionais
              no beneficiamento dessas cargas.
            </div>
            <div className="text-xs text-amber-800 mt-1">
              Abra cada NF marcada com <AlertTriangle size={11} className="inline text-amber-700 -mt-0.5" /> e preencha o preço do beneficiamento (Boca de máquina ou Balanção) e os custos (frete, maquinação, sacaria, chapa, ICMS) para o CMV entrar no relatório.
            </div>
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <Card className="p-6 mb-6 print:hidden">
          <h3 className="font-heading font-bold text-ink-900 mb-3">Top 10 por lucro</h3>
          <div className="h-72 min-h-[280px]">
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                <XAxis type="number" stroke="#5E5E5E" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" stroke="#5E5E5E" tick={{ fontSize: 11 }} width={120} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E2DC", fontSize: 12 }} formatter={(v) => fmtBRL(v)} />
                <Bar dataKey="lucro" radius={[0,4,4,0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.lucro >= 0 ? "#1E3F33" : "#D32F2F"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {filteredRows.length === 0 ? (
        <Card><div className="text-center py-12 text-sm text-ink-500">Sem dados no período{fornFilter ? " para esse fornecedor" : ""}.</div></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-tertiary">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                {kind === "invoice" && <><th className="px-4 py-3">NF</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Fornecedor</th><th className="px-4 py-3">Tipo</th></>}
                {kind === "client" && <><th className="px-4 py-3">Cliente</th><th className="px-4 py-3 text-right">Saídas</th></>}
                {kind === "supplier" && <><th className="px-4 py-3">Fornecedor</th><th className="px-4 py-3 text-right">NFs</th></>}
                <th className="px-4 py-3 text-right">Caixas vend.</th>
                <th className="px-4 py-3 text-right">Receita</th>
                <th className="px-4 py-3 text-right">CMV</th>
                <th className="px-4 py-3 text-right">Lucro</th>
                <th className="px-4 py-3 text-right">Margem</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => (
                <tr
                  key={i}
                  className={`border-b border-tertiary last:border-0 transition-colors ${r.custo_incompleto ? "bg-amber-50/60 hover:bg-amber-50" : "hover:bg-stone-50"} ${kind === "invoice" ? "cursor-pointer" : ""}`}
                  onClick={kind === "invoice" ? () => setDrillId(r.invoice_id) : undefined}
                  data-testid={kind === "invoice" ? `pnl-invoice-row-${r.nota_numero}` : undefined}
                  title={kind === "invoice" ? (r.custo_incompleto ? "Custo não lançado — clique para ver detalhes" : "Clique para ver o raio-X desta carga") : undefined}
                >
                  {kind === "invoice" && <>
                    <td className="px-4 py-3 font-mono">
                      <span className="inline-flex items-center gap-1.5">
                        {r.custo_incompleto && (
                          <AlertTriangle
                            size={13}
                            className="text-amber-600"
                            data-testid={`pnl-invoice-custo-incompleto-${r.nota_numero}`}
                          />
                        )}
                        #{r.nota_numero}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-600 whitespace-nowrap">
                      {r.data ? (() => { try { const [y,m,day] = r.data.split("-"); return `${day}/${m}/${y}`; } catch { return r.data; } })() : "—"}
                    </td>
                    <td className="px-4 py-3">{r.fornecedor}</td>
                    <td className="px-4 py-3 capitalize">{r.tipo_cebola}</td>
                  </>}
                  {kind === "client" && <>
                    <td className="px-4 py-3">{r.cliente}</td>
                    <td className="px-4 py-3 font-mono text-right">{r.n_saidas}</td>
                  </>}
                  {kind === "supplier" && <>
                    <td className="px-4 py-3">{r.fornecedor}</td>
                    <td className="px-4 py-3 font-mono text-right">{r.n_nfs}</td>
                  </>}
                  <td className="px-4 py-3 font-mono text-right">
                    {fmtNum(r.caixas_vendidas || r.caixas || 0, 0)}
                    {r.unidade_medida === "kg" && <span className="text-ink-500 ml-1">kg</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.receita)}</td>
                  <td className="px-4 py-3 font-mono text-right text-ink-500">
                    {r.custo_incompleto ? <span className="text-amber-700 italic">não lançado</span> : fmtBRL(r.cmv_alocado || r.cmv || 0)}
                  </td>
                  <td className={`px-4 py-3 font-mono text-right font-semibold ${r.lucro >= 0 ? "text-ok" : "text-danger"}`}>
                    <span className="inline-flex items-center gap-1">
                      {r.lucro > 0 ? <TrendingUp size={12} /> : r.lucro < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                      {fmtBRL(r.lucro)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-right">{r.margem_pct || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {drillId && <InvoicePnlDrill invoiceId={drillId} onClose={() => setDrillId(null)} />}
    </>
  );
}

function WeeklyView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/reports/weekly-balance").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  const c = data?.current;
  if (!c) return null;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="p-6 border-emerald-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ok">+ Positivos</div>
          <div className="font-mono text-2xl font-semibold text-ok mt-2">{fmtBRL(c.positivos.total)}</div>
          <div className="mt-3 space-y-1 text-sm">
            <Row label="A receber em aberto" value={fmtBRL(c.positivos.ar_aberto)} />
            <Row label={`Estoque (${fmtNum(c.positivos.estoque_caixas, 0)} cx)`} value={fmtBRL(c.positivos.estoque_valor)} />
          </div>
        </Card>
        <Card className="p-6 border-red-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-danger">− Negativos</div>
          <div className="font-mono text-2xl font-semibold text-danger mt-2">{fmtBRL(c.negativos.total)}</div>
          <div className="mt-3 space-y-1 text-sm">
            <Row label="A pagar em aberto" value={fmtBRL(c.negativos.ap_aberto)} />
          </div>
        </Card>
        <div className={`p-6 rounded-lg shadow-sm border ${c.saldo >= 0 ? "bg-brand-500 border-brand-500" : "bg-danger border-danger"}`}>
          <div className={`text-xs font-bold uppercase tracking-[0.1em] ${c.saldo >= 0 ? "text-emerald-100" : "text-red-100"}`}>Saldo Patrimonial</div>
          <div className="font-mono text-3xl font-extrabold text-white mt-2" data-testid="weekly-saldo">{fmtBRL(c.saldo)}</div>
          <div className={`text-xs mt-1 ${c.saldo >= 0 ? "text-emerald-100" : "text-red-100"}`}>Ref. {c.data}</div>
        </div>
      </div>

      {(data?.history || []).length > 1 && (
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b border-tertiary">
            <h3 className="font-heading font-bold text-ink-900">Histórico semanal</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-tertiary">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Semana</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3 text-right">Positivos</th>
                <th className="px-4 py-3 text-right">Negativos</th>
                <th className="px-4 py-3 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {(data?.history || []).map((s) => (
                <tr key={s.week_id} className="border-b border-tertiary last:border-0">
                  <td className="px-4 py-3 font-mono">{s.week_id}</td>
                  <td className="px-4 py-3 font-mono">{s.data}</td>
                  <td className="px-4 py-3 font-mono text-right text-ok">{fmtBRL(s.positivos?.total || 0)}</td>
                  <td className="px-4 py-3 font-mono text-right text-danger">{fmtBRL(s.negativos?.total || 0)}</td>
                  <td className={`px-4 py-3 font-mono text-right font-semibold ${(s.saldo || 0) >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(s.saldo || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-ink-500">{label}</span>
      <span className="font-mono font-medium text-ink-900">{value}</span>
    </div>
  );
}


function TerceirosReport({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    setLoading(true); setErr("");
    api.get("/reports/terceiros/top-clientes", { params: { from_: from, to, limit: 10 } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);
  if (loading) return <div className="flex justify-center py-10"><Spinner size={24} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  if (!data) return null;
  const rows = data.items || [];
  const totals = data.totals || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Clientes</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{totals.clientes || 0}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">NFs beneficiadas</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{totals.nfs || 0}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Total sacos</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{fmtNum(totals.sacos || 0, 0)}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Faturado (R$)</div>
          <div className="font-mono text-2xl font-bold text-purple-800 mt-1">{fmtBRL(totals.valor || 0)}</div>
        </Card>
      </div>

      {rows.length > 0 && (
        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3">Top 10 Clientes por Sacos Beneficiados</div>
          <div style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={Math.max(280, rows.length * 44)}>
              <BarChart data={rows} layout="vertical" margin={{ left: 30, right: 20, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis type="category" dataKey="cliente_dono" width={160} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtNum(v, 0) + " sacos"} />
                <Bar dataKey="sacos" fill="#7c3aed" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-ink-200">
            <tr className="text-xs font-bold uppercase tracking-wider text-ink-500">
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-right">NFs</th>
              <th className="px-4 py-3 text-right">Sacos</th>
              <th className="px-4 py-3 text-right">Serviço</th>
              <th className="px-4 py-3 text-right">Sacaria</th>
              <th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-ink-500">Nenhum dado de terceiros no período.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.cliente_dono} className="border-b border-ink-200 hover:bg-purple-50/30" data-testid={`terceiros-row-${i}`}>
                <td className="px-4 py-3 font-mono text-ink-500">{i + 1}</td>
                <td className="px-4 py-3 font-semibold text-purple-900">{r.cliente_dono}</td>
                <td className="px-4 py-3 text-right font-mono">{r.nfs}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{fmtNum(r.sacos, 0)}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtBRL(r.valor_servico)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-ink-500">{r.valor_sacaria ? fmtBRL(r.valor_sacaria) : "—"}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-purple-800">{fmtBRL(r.valor_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


function ProducerStatementView({ from, to }) {
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedor, setFornecedor] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reports/pnl/by-supplier");
        const nomes = [...new Set((r.data?.rows || []).map((x) => x.fornecedor).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, "pt-BR"));
        setFornecedores(nomes);
      } catch { /* silêncio */ }
    })();
  }, []);

  const carregar = async () => {
    if (!fornecedor) return;
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ fornecedor });
      if (from) params.set("from_", from);
      if (to) params.set("to", to);
      const r = await api.get(`/reports/producer-statement?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  const totais = data?.totais || {};

  return (
    <div className="producer-print">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm 10mm; }
          html, body { font-size: 10pt; }
          .producer-print { color: #000; }
          .producer-print h1 { font-size: 16pt; margin-bottom: 4px; }
          .producer-print table { font-size: 9pt; border-collapse: collapse; width: 100%; }
          .producer-print thead { display: table-header-group; }
          .producer-print thead tr { background: #f3f3f3 !important; }
          .producer-print th, .producer-print td { padding: 2px 6px !important; line-height: 1.25 !important; }
          .producer-print tbody tr { page-break-inside: avoid; }
          .producer-print tbody tr:nth-child(even) { background: #fafafa !important; }
          .producer-print tfoot tr { background: #eaeaea !important; }
          .producer-print .kpi-print-row { display: grid !important; grid-template-columns: repeat(5, 1fr); gap: 4px; margin-bottom: 6px !important; }
          .producer-print .kpi-print-row > div {
            border: 1px solid #ddd; padding: 4px 6px; border-radius: 4px;
            box-shadow: none !important;
          }
          .producer-print .kpi-print-row .kpi-val { font-size: 11pt; margin-top: 0 !important; }
          .producer-print .signatures { margin-top: 20mm !important; }
        }
      `}</style>
      <Card className="p-5 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">
              Produtor / Fornecedor
            </label>
            <input
              type="text"
              list="produtor-list"
              value={fornecedor}
              onChange={(e) => setFornecedor(e.target.value)}
              placeholder="Digite ou selecione o produtor"
              className="w-full h-10 rounded-md border border-ink-200 px-3 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
              data-testid="producer-statement-fornecedor"
            />
            <datalist id="produtor-list">
              {fornecedores.map((f) => <option key={f} value={f} />)}
            </datalist>
          </div>
          <button
            onClick={carregar}
            disabled={!fornecedor || loading}
            className="h-10 px-5 rounded-md bg-brand-500 hover:bg-brand-600 disabled:bg-stone-300 text-white text-sm font-semibold uppercase tracking-wider transition-colors"
            data-testid="producer-statement-load"
          >
            {loading ? <Spinner size={16} /> : "Carregar"}
          </button>
        </div>
        {err && <div className="mt-3 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
      </Card>

      {data && (
        <>
          <div className="hidden print:block mb-6">
            <h1 className="text-2xl font-bold">Extrato do Produtor</h1>
            <div className="text-sm mt-1"><b>Produtor:</b> {data.fornecedor}</div>
            <div className="text-sm"><b>Período:</b> {from || "início"} até {to || "hoje"}</div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6 kpi-print-row">
            <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Cargas</div><div className="font-mono text-xl font-semibold text-ink-900 mt-1 kpi-val" data-testid="producer-statement-total-cargas">{fmtNum(totais.n_cargas || 0, 0)}</div></Card>
            <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Peso acertado</div><div className="font-mono text-xl font-semibold text-emerald-700 mt-1 kpi-val" data-testid="producer-statement-total-pagavel">{fmtNum(totais.peso_pagavel || 0, 0)} kg</div></Card>
            <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Preço médio</div><div className="font-mono text-xl font-semibold text-brand-700 mt-1 kpi-val" data-testid="producer-statement-preco-medio">{totais.preco_medio_kg > 0 ? fmtBRL(totais.preco_medio_kg) : "—"}<span className="text-xs text-ink-500 ml-1">/kg</span></div></Card>
            <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Valor acertado</div><div className="font-mono text-xl font-semibold text-ink-900 mt-1 kpi-val" data-testid="producer-statement-total-acertado">{fmtBRL(totais.valor_acertado_total || 0)}</div></Card>
            <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Funrural</div><div className="font-mono text-xl font-semibold text-red-700 mt-1 kpi-val" data-testid="producer-statement-total-funrural">− {fmtBRL(totais.funrural_total || 0)}</div></Card>
            <Card className="p-4 bg-emerald-50 border-emerald-300"><div className="text-xs font-bold uppercase tracking-[0.1em] text-emerald-900">Valor líquido</div><div className="font-mono text-xl font-extrabold text-emerald-900 mt-1 kpi-val" data-testid="producer-statement-total-liquido">{fmtBRL(totais.valor_liquido_total || 0)}</div></Card>
          </div>

          <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid="producer-statement-table">
              <thead className="bg-stone-50 border-b border-tertiary">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">NF</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3 text-right">Peso Líquido</th>
                  <th className="px-4 py-3 text-right">Desc. Balanção</th>
                  <th className="px-4 py-3 text-right">Peso Acertado</th>
                  <th className="px-4 py-3 text-right">Preço/kg</th>
                  <th className="px-4 py-3 text-right">Valor Acertado</th>
                  <th className="px-4 py-3 text-right">Funrural %</th>
                  <th className="px-4 py-3 text-right">Funrural R$</th>
                  <th className="px-4 py-3 text-right">Valor Líquido</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r) => (
                  <tr key={r.invoice_id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`producer-statement-row-${r.nota_numero}`}>
                    <td className="px-4 py-3 font-mono whitespace-nowrap">{r.data ? new Date(r.data + "T12:00").toLocaleDateString("pt-BR") : "—"}</td>
                    <td className="px-4 py-3 font-mono">#{r.nota_numero}</td>
                    <td className="px-4 py-3 capitalize">{r.tipo_cebola}</td>
                    <td className="px-4 py-3 font-mono text-right font-semibold">{fmtNum(r.peso_liquido, 0)} kg</td>
                    <td className="px-4 py-3 font-mono text-right text-amber-700">{fmtNum(r.quebra_pct, 2)}%</td>
                    <td className="px-4 py-3 font-mono text-right font-bold text-emerald-700">{fmtNum(r.peso_pagavel, 0)} kg</td>
                    <td className="px-4 py-3 font-mono text-right text-brand-700">{r.preco_kg > 0 ? fmtBRL(r.preco_kg) : "—"}</td>
                    <td className="px-4 py-3 font-mono text-right font-semibold">{r.valor_acertado > 0 ? fmtBRL(r.valor_acertado) : "—"}</td>
                    <td className="px-4 py-3 font-mono text-right text-ink-500">{fmtNum(r.fun_rural_pct, 2)}%</td>
                    <td className="px-4 py-3 font-mono text-right text-red-700">{fmtBRL(r.funrural_valor)}</td>
                    <td className="px-4 py-3 font-mono text-right font-bold text-emerald-700">{fmtBRL(r.valor_liquido_calc || 0)}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-8 text-center text-ink-500">Nenhuma carga encontrada para este produtor no período selecionado.</td></tr>
                )}
              </tbody>
              {data.rows.length > 0 && (
                <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold">
                  <tr>
                    <td className="px-4 py-3" colSpan={3}>TOTAL</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtNum(totais.peso_liquido, 0)} kg</td>
                    <td className="px-4 py-3 font-mono text-right text-amber-700">{fmtNum(totais.quebra_media_pct, 2)}%</td>
                    <td className="px-4 py-3 font-mono text-right text-emerald-700">{fmtNum(totais.peso_pagavel, 0)} kg</td>
                    <td className="px-4 py-3 font-mono text-right text-brand-700 text-[11px]">méd. {totais.preco_medio_kg > 0 ? fmtBRL(totais.preco_medio_kg) : "—"}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(totais.valor_acertado_total || 0)}</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-mono text-right text-red-700">{fmtBRL(totais.funrural_total)}</td>
                    <td className="px-4 py-3 font-mono text-right text-emerald-700">{fmtBRL(totais.valor_liquido_total || 0)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </Card>

          <div className="hidden print:block mt-16 signatures">
            <div className="grid grid-cols-2 gap-16">
              <div className="text-center">
                <div className="border-t border-black pt-2 text-xs">Cebolas Serenata</div>
              </div>
              <div className="text-center">
                <div className="border-t border-black pt-2 text-xs">{data.fornecedor}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AllProducersModal({ onClose, onPick }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [onlyEligible, setOnlyEligible] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/reports/producers-detailed");
        if (alive) setList(r.data || []);
      } catch (e) {
        if (alive) setErr(formatErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Fecha com ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return list.filter((p) => {
      if (onlyEligible && !p.elegivel) return false;
      if (!q) return true;
      return String(p.nome || "").toUpperCase().includes(q);
    });
  }, [list, search, onlyEligible]);

  const totalElegiveis = list.filter((p) => p.elegivel).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="producer-target-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ink-200">
          <div>
            <h3 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
              <Users size={18} /> Fornecedores cadastrados
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">
              Clique em um nome para carregar o relatório dele.
              {!loading && (
                <span className="ml-1">
                  <b>{totalElegiveis}</b> de {list.length} têm NFs beneficiadas próprias (elegíveis).
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-ink-100 text-ink-500"
            aria-label="Fechar"
            data-testid="producer-target-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filtros */}
        <div className="p-3 border-b border-ink-100 flex flex-wrap items-center gap-3 bg-ink-50/40">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome..."
              className="w-full rounded-md border border-ink-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              data-testid="producer-target-modal-search"
              autoFocus
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-ink-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyEligible}
              onChange={(e) => setOnlyEligible(e.target.checked)}
              className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
              data-testid="producer-target-modal-only-eligible"
            />
            Só elegíveis (têm NF beneficiada)
          </label>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-ink-500 text-sm gap-2">
              <Spinner size={16} /> Carregando...
            </div>
          ) : err ? (
            <div className="p-4 text-sm text-danger">{err}</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-ink-500 italic">
              Nenhum fornecedor encontrado com esse filtro.
            </div>
          ) : (
            <ul className="divide-y divide-ink-100">
              {filtered.map((p) => (
                <li
                  key={p.nome}
                  className={`px-3 py-2.5 flex items-center justify-between gap-3 rounded transition ${
                    p.elegivel
                      ? "hover:bg-brand-50/60 cursor-pointer"
                      : "opacity-70 cursor-not-allowed bg-ink-50/30"
                  }`}
                  onClick={() => { if (p.elegivel) onPick(p.nome); }}
                  title={p.elegivel ? "Clique para carregar" : "Sem NFs beneficiadas próprias — não gera relatório"}
                  data-testid={`producer-target-modal-item-${p.nome.replace(/\s+/g, "-")}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {p.elegivel && (
                        <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                      )}
                      {!p.elegivel && (
                        <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                      )}
                      <span className="font-medium text-sm text-ink-900 truncate">
                        {p.nome}
                      </span>
                    </div>
                    <div className="text-[11px] text-ink-500 mt-1 ml-6 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>Total NFs: <b className="font-mono text-ink-700">{p.total}</b></span>
                      {Object.entries(p.por_status || {}).map(([s, c]) => (
                        <span key={s}>
                          {s}: <b className="font-mono text-ink-700">{c}</b>
                        </span>
                      ))}
                      {(p.tipos || []).includes("terceiros") && (
                        <span className="text-amber-700">inclui terceiros</span>
                      )}
                    </div>
                  </div>
                  {p.elegivel && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded shrink-0">
                      {p.beneficiada_propria} elegível{p.beneficiada_propria > 1 ? "is" : ""}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-ink-100 bg-ink-50/40 flex items-center justify-between text-xs text-ink-500">
          <span>
            <CheckCircle2 size={12} className="inline text-emerald-600 mr-1" />
            = tem pelo menos 1 NF beneficiada própria (aparece no relatório).
          </span>
          <button
            onClick={onClose}
            className="text-ink-600 hover:text-ink-900 underline"
          >
            Fechar (Esc)
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL = { bruto: "bruta (aguardando)", em_beneficiamento: "em beneficiamento", beneficiada: "beneficiada" };

function explainTargetEmpty(nome, dg) {
  if (!dg) return `Nenhuma NF beneficiada encontrada para "${nome}".`;
  if (!dg.total_nfs) {
    const sim = (dg.nomes_similares || []).join(", ");
    return `Nenhuma NF de compra com o nome "${nome}".${sim ? ` Nomes parecidos cadastrados: ${sim}.` : " Use \"Ver todos os fornecedores\" para conferir o nome exato."}`;
  }
  const status = Object.entries(dg.por_status || {}).map(([s, c]) => `${c} ${STATUS_LABEL[s] || s}`).join(", ");
  const partes = [`"${nome}" tem ${dg.total_nfs} NF(s): ${status}.`];
  if (dg.beneficiadas_fora_periodo) {
    const [a, b] = dg.fora_periodo_datas || [];
    partes.push(`${dg.beneficiadas_fora_periodo} beneficiada(s) estão FORA do período selecionado (beneficiadas entre ${fmtDateBR(a)} e ${fmtDateBR(b)}) — ajuste as datas no topo.`);
  }
  if (dg.terceiros) partes.push(`${dg.terceiros} são de beneficiamento para terceiros (não entram no cálculo).`);
  if (!dg.por_status?.beneficiada) partes.push("Nenhuma foi beneficiada ainda — o preço alvo só é calculado após finalizar o beneficiamento.");
  return partes.join(" ");
}


function ProducerTargetPriceView({ from, to }) {
  const [fornecedor, setFornecedor] = useState("");
  const [margemPct, setMargemPct] = useState("10");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const [producers, setProducers] = useState([]);
  const [showAllModal, setShowAllModal] = useState(false);

  // Carrega lista de produtores para autocomplete
  useEffect(() => {
    api.get("/reports/producers")
      .then((r) => setProducers(r.data || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    if (!fornecedor.trim()) { setErr("Digite o nome do produtor"); return; }
    setLoading(true); setErr(""); setProgress(0);
    // Barra de progresso "elástica" — sobe até 90% enquanto espera resposta e vai a 100% ao terminar
    let cancelled = false;
    const tick = () => {
      setProgress((p) => {
        if (cancelled || p >= 90) return p;
        const step = p < 30 ? 4 : p < 60 ? 2.5 : p < 80 ? 1.2 : 0.5;
        return Math.min(90, p + step);
      });
    };
    const iv = setInterval(tick, 120);
    try {
      const params = new URLSearchParams();
      params.set("fornecedor", fornecedor.trim());
      params.set("margem_pct", String(Number(margemPct) || 0));
      if (from) params.set("from_", from);
      if (to) params.set("to", to);
      const r = await api.get(`/reports/producer-target-price?${params.toString()}`);
      setData(r.data);
      setProgress(100);
      // aviso amigável quando não encontra nada
      if (!(r.data?.rows || []).length) {
        setErr(explainTargetEmpty(fornecedor.trim(), r.data?.diagnostico));
      }
    } catch (e) { setErr(formatErr(e)); }
    finally {
      cancelled = true;
      clearInterval(iv);
      // Pequeno delay pra barra chegar visualmente a 100%
      setTimeout(() => { setLoading(false); setProgress(0); }, 300);
    }
  };

  const t = data?.totais || {};
  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]" data-testid="producer-target-search-wrap">
            <label className="text-[10px] uppercase tracking-wider text-ink-500 font-bold block mb-1">Produtor / Fornecedor</label>
            <input
              type="text"
              value={fornecedor}
              onChange={(e) => setFornecedor(e.target.value)}
              list="producer-target-suggestions"
              placeholder="Comece a digitar... (busca não diferencia maiúsculas)"
              className="w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              data-testid="producer-target-fornecedor"
              onKeyDown={(e) => e.key === "Enter" && load()}
              disabled={loading}
            />
            <datalist id="producer-target-suggestions">
              {producers.map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-ink-500 font-bold block mb-1">Margem alvo (%)</label>
            <input
              type="number"
              step="0.5"
              value={margemPct}
              onChange={(e) => setMargemPct(e.target.value)}
              className="w-24 rounded-md border border-ink-300 px-3 py-2 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
              data-testid="producer-target-margem"
              disabled={loading}
            />
          </div>
          <Button onClick={load} disabled={loading} data-testid="producer-target-load">
            {loading ? <Spinner size={16} /> : "Calcular"}
          </Button>
          <Button
            onClick={() => setShowAllModal(true)}
            disabled={loading}
            className="!bg-white !text-ink-700 !border !border-ink-300 hover:!bg-ink-50"
            data-testid="producer-target-show-all-btn"
          >
            <Users size={14} className="mr-1.5" /> Ver todos os fornecedores
          </Button>
        </div>
        {/* Barra de progresso */}
        {loading && (
          <div className="mt-3" data-testid="producer-target-progress">
            <div className="flex items-center justify-between text-xs text-ink-600 mb-1">
              <span>Carregando cargas do produtor...</span>
              <span className="font-mono">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 w-full bg-ink-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-brand-500 to-emerald-500 rounded-full transition-all duration-150 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
        {err && !loading && <div className="mt-3 text-sm text-danger">{err}</div>}
      </Card>

      {/* Modal: Todos os fornecedores cadastrados */}
      {showAllModal && (
        <AllProducersModal
          onClose={() => setShowAllModal(false)}
          onPick={(nome) => { setFornecedor(nome); setShowAllModal(false); setErr(""); }}
        />
      )}

      {/* Mantém resultado anterior visível enquanto carrega o novo */}
      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-500 font-bold">Cargas com venda</div>
              <div className="font-mono text-xl font-semibold text-ink-900 mt-1">
                {t.n_cargas_com_venda || 0}
                {t.n_cargas > t.n_cargas_com_venda && (
                  <span className="text-xs text-ink-500 font-normal ml-1">
                    (+{(t.n_cargas || 0) - (t.n_cargas_com_venda || 0)} sem vendas)
                  </span>
                )}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-500 font-bold">Preço médio pago</div>
              <div className="font-mono text-xl font-semibold text-ink-900 mt-1" data-testid="producer-target-preco-atual">
                R$ {Number(t.preco_medio_atual || 0).toFixed(4)}<span className="text-xs text-ink-500 ml-1">/kg</span>
              </div>
              <div className="text-[11px] text-ink-500">Total R$ {Number(t.valor_pago_atual || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
            </Card>
            <Card className={`p-4 border-2 ${Number(t.preco_medio_alvo || 0) > 0 ? "border-brand-500 bg-brand-500/5" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider text-brand-700 font-bold">
                Preço ALVO (p/ {data.margem_pct}%)
              </div>
              <div className="font-mono text-xl font-semibold text-brand-700 mt-1" data-testid="producer-target-preco-alvo">
                R$ {Number(t.preco_medio_alvo || 0).toFixed(4)}<span className="text-xs text-brand-600 ml-1">/kg</span>
              </div>
              <div className="text-[11px] text-brand-600">Total R$ {Number(t.valor_alvo_produtor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
            </Card>
            <Card className={`p-4 ${Number(t.diff_valor || 0) > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${Number(t.diff_valor || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
                {Number(t.diff_valor || 0) > 0 ? "Pagou A MAIS" : "Ainda pode pagar"}
              </div>
              <div className={`font-mono text-xl font-semibold mt-1 ${Number(t.diff_valor || 0) > 0 ? "text-red-800" : "text-emerald-800"}`} data-testid="producer-target-diff">
                R$ {Math.abs(Number(t.diff_valor || 0)).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </div>
              <div className={`text-[11px] ${Number(t.diff_valor || 0) > 0 ? "text-red-600" : "text-emerald-600"}`}>
                {Number(t.diff_valor || 0) > 0 ? "= reduzir margem" : "= sobra p/ negociação"}
              </div>
            </Card>
          </div>

          {/* Explicação da fórmula */}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
            <strong>Cálculo:</strong> Preço ALVO = (Receita REALIZADA das vendas, sem projeção × {(100 - data.margem_pct).toFixed(0)}%) − Custos operacionais (chapa, ICMS, frete, maquinação, sacaria). Depois dividido pelo peso pagável. NFs sem vendas são ignoradas dos totais.<br/>
            <strong className="text-amber-900">⚠ Atenção:</strong> as colunas <em>"Pago produtor"</em> e <em>"Alvo produtor"</em> mostram apenas o valor destinado ao produtor. O <em>"Custo total"</em> (produtor + custos operacionais) aparece em coluna separada.
          </div>

          {/* Tabela detalhada */}
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ink-50/70 text-[11px] uppercase tracking-wider text-ink-500">
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-left">NF</th>
                    <th className="px-3 py-2 text-right">Peso pag.</th>
                    <th className="px-3 py-2 text-right">% vend.</th>
                    <th className="px-3 py-2 text-right">% perda</th>
                    <th className="px-3 py-2 text-right">% estoque</th>
                    <th className="px-3 py-2 text-right">Receita real</th>
                    <th className="px-3 py-2 text-right">Custos op.</th>
                    <th className="px-3 py-2 text-right">Pago (R$/kg)</th>
                    <th className="px-3 py-2 text-right">Alvo (R$/kg)</th>
                    <th className="px-3 py-2 text-right">Pago produtor</th>
                    <th className="px-3 py-2 text-right">Custo TOTAL</th>
                    <th className="px-3 py-2 text-right">Alvo produtor</th>
                    <th className="px-3 py-2 text-right">Diff</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-200">
                  {(data.rows || []).map((r) => {
                    const semVenda = r.sem_vendas;
                    const diffPos = r.diff_valor > 0;
                    return (
                      <tr key={r.invoice_id} className={semVenda ? "bg-ink-50/50 text-ink-500 italic" : "hover:bg-ink-50/40"} data-testid={`producer-target-row-${r.invoice_id}`}>
                        <td className="px-3 py-2 whitespace-nowrap">{(r.data || "").split("-").reverse().join("/")}</td>
                        <td className="px-3 py-2 font-mono font-semibold">#{r.nota_numero}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(r.peso_pagavel || 0, 0)} kg</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{r.pct_vendido}%</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${r.pct_perdido > 0 ? "text-red-700 font-semibold" : "text-ink-400"}`} data-testid={`producer-target-perda-${r.invoice_id}`}>{r.pct_perdido ?? 0}%</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${r.pct_estoque > 0 ? "text-amber-700 font-semibold" : "text-ink-400"}`} data-testid={`producer-target-estoque-${r.invoice_id}`}>{r.pct_estoque ?? 0}%</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtBRL(r.receita_projetada)}</td>
                        <td className="px-3 py-2 text-right font-mono text-ink-500">{fmtBRL(r.custos_operacionais)}</td>
                        <td className="px-3 py-2 text-right font-mono">R$ {Number(r.preco_kg_atual).toFixed(4)}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-brand-700">
                          {semVenda ? "—" : `R$ ${Number(r.preco_kg_alvo).toFixed(4)}`}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{fmtBRL(r.valor_pago_atual)}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          {fmtBRL(r.valor_pago_atual + r.custos_operacionais)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-brand-700">
                          {semVenda ? "—" : fmtBRL(r.valor_alvo_produtor)}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${semVenda ? "" : diffPos ? "text-red-700" : "text-emerald-700"}`}>
                          {semVenda ? "sem vendas" : `${diffPos ? "+" : ""}${fmtBRL(r.diff_valor)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-ink-50 font-bold border-t-2 border-ink-300">
                    <td className="px-3 py-2" colSpan={6}>Total ({t.n_cargas_com_venda || 0} cargas com venda)</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtBRL(t.receita_projetada)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtBRL(t.custos_operacionais)}</td>
                    <td className="px-3 py-2 text-right font-mono">R$ {Number(t.preco_medio_atual || 0).toFixed(4)}</td>
                    <td className="px-3 py-2 text-right font-mono text-brand-700">R$ {Number(t.preco_medio_alvo || 0).toFixed(4)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtBRL(t.valor_pago_atual)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtBRL((t.valor_pago_atual || 0) + (t.custos_operacionais || 0))}</td>
                    <td className="px-3 py-2 text-right font-mono text-brand-700">{fmtBRL(t.valor_alvo_produtor)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${Number(t.diff_valor || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {fmtBRL(t.diff_valor)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function CustoPorSacoView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [drillDay, setDrillDay] = useState(null); // {data, proprios, terceiros, totais}
  const [drillLoading, setDrillLoading] = useState(false);
  // Custo variável por saco pra unidades terceirizadas (aluga máquina/mão de obra)
  // Persistido em localStorage; padrão Araxá R$ 3,00/saco
  const [terceirizados, setTerceirizados] = useState(() => {
    try {
      const raw = localStorage.getItem("cps_terceirizados");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { "Araxá": 3.0 };
  });
  useEffect(() => {
    try { localStorage.setItem("cps_terceirizados", JSON.stringify(terceirizados)); } catch { /* ignore */ }
  }, [terceirizados]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const params = new URLSearchParams();
        if (from) params.set("from_", from);
        if (to) params.set("to", to);
        const r = await api.get(`/reports/custo-por-saco?${params.toString()}`);
        if (!ignore) setData(r.data);
      } catch (e) {
        if (!ignore) setErr(e?.response?.data?.detail || "Erro ao carregar");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [from, to]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;
  if (err) return <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>;
  if (!data) return null;

  const sacosPorUnidade = data.producao?.sacos_por_unidade || {};
  // Custo terceirizado = soma (sacos da unidade × R$/saco configurado)
  const custoTerceirizadoDetalhe = Object.entries(terceirizados)
    .filter(([un, v]) => Number(v) > 0 && (sacosPorUnidade[un] || 0) > 0)
    .map(([un, v]) => ({
      unidade: un,
      sacos: sacosPorUnidade[un] || 0,
      preco: Number(v),
      total: (sacosPorUnidade[un] || 0) * Number(v),
    }));
  const custoTerceirizado = custoTerceirizadoDetalhe.reduce((s, x) => s + x.total, 0);

  const totalCusto = (data.resultado?.custo_total_periodo || 0) + custoTerceirizado;
  const sacos = data.producao?.sacos_beneficiados || 0;
  const cps = sacos > 0 ? totalCusto / sacos : 0;

  const unidadesConhecidas = Array.from(new Set([...Object.keys(sacosPorUnidade), ...Object.keys(terceirizados)]));

  return (
    <div>
      {/* KPI destaque */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 md:col-span-1 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <div className="text-xs font-bold uppercase tracking-[0.1em] opacity-80">Custo por Saco Maquinado</div>
          <div className="font-mono text-4xl font-black mt-2" data-testid="custo-por-saco-valor">{fmtBRL(cps)}</div>
          <div className="text-xs opacity-80 mt-2">
            {fmtBRL(totalCusto)} ÷ {fmtNum(sacos, 0)} sacos
          </div>
          <div className="text-[11px] opacity-80 mt-2 border-t border-white/20 pt-2 space-y-0.5">
            <div className="flex justify-between"><span>Próprios</span><span className="font-mono">{fmtNum(data.producao?.sacos_proprios || 0, 0)}</span></div>
            <div className="flex justify-between"><span>Terceiros (maquinação)</span><span className="font-mono">{fmtNum(data.producao?.sacos_terceiros || 0, 0)}</span></div>
            {Object.keys(sacosPorUnidade).length > 0 && (
              <div className="pt-1 mt-1 border-t border-white/10">
                {Object.entries(sacosPorUnidade).map(([un, q]) => (
                  <div key={un} className="flex justify-between opacity-90"><span>{un}</span><span className="font-mono">{fmtNum(q, 0)}</span></div>
                ))}
              </div>
            )}
          </div>
          <div className="text-[11px] opacity-70 mt-2">
            Período: {data.meses} {data.meses === 1 ? "mês" : "meses"}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Despesas recorrentes</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-1.5" data-testid="cps-recorrentes">{fmtBRL(data.recorrentes?.valor_periodo)}</div>
          <div className="text-xs text-ink-500 mt-1">{fmtBRL(data.recorrentes?.valor_mes)} / mês</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Folha (salários)</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-1.5" data-testid="cps-folha">{fmtBRL(data.folha?.valor_periodo)}</div>
          <div className="text-xs text-ink-500 mt-1">
            {fmtBRL(data.folha?.media_mensal)} / mês média
            {data.folha?.n_folhas > 0 ? ` • ${data.folha.n_folhas} folha(s) fechada(s)` : " • fallback: salário base"}
          </div>
        </Card>
      </div>

      {/* Composição do custo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Custo variável de terceirização por unidade */}
          <Card className="p-5" data-testid="cps-terceirizacao-card">
            <div className="font-bold text-ink-900 text-sm uppercase tracking-wider mb-1">Terceirização por Unidade</div>
            <div className="text-xs text-ink-500 mb-4">Unidades onde você paga um valor fixo por saco maquinado (sem estrutura própria)</div>
            <div className="space-y-2">
              {unidadesConhecidas.map((un) => (
                <div key={un} className="grid grid-cols-[1fr_120px_120px] gap-2 items-center">
                  <div className="text-sm font-semibold text-ink-900">{un}</div>
                  <div className="flex items-center gap-1 text-xs text-ink-500 font-mono">
                    <span>R$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={terceirizados[un] ?? ""}
                      onChange={(e) => {
                        const v = e.target.value === "" ? 0 : Number(e.target.value);
                        setTerceirizados({ ...terceirizados, [un]: v });
                      }}
                      placeholder="0,00"
                      className="w-full h-8 rounded border border-ink-200 px-2 text-right text-sm font-mono focus:ring-1 focus:ring-brand-500 outline-none"
                      data-testid={`cps-terc-input-${un}`}
                    />
                    <span>/sc</span>
                  </div>
                  <div className="text-right font-mono text-xs text-ink-500">
                    {(sacosPorUnidade[un] || 0) > 0
                      ? `× ${fmtNum(sacosPorUnidade[un], 0)} = ${fmtBRL((sacosPorUnidade[un] || 0) * (Number(terceirizados[un]) || 0))}`
                      : "sem produção"}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-ink-500 bg-stone-50 rounded p-3 border border-stone-200">
              <b>Como funciona:</b> unidades como <b>Araxá</b> onde você paga um valor fixo por saco (ex.: R$ 3,00) entram no cálculo como custo variável. O valor é salvo no seu navegador — cada usuário pode ter seu preço combinado.
            </div>
          </Card>

          {/* Recorrentes */}
          <Card className="overflow-hidden">
            <div className="px-5 py-4 border-b border-tertiary">
              <div className="font-bold text-ink-900 text-sm uppercase tracking-wider">Despesas Recorrentes Ativas</div>
              <div className="text-xs text-ink-500 mt-0.5">Valor mensal • ordenado por peso</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-tertiary">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-2.5">Nome</th>
                  <th className="px-4 py-2.5">Categoria</th>
                  <th className="px-4 py-2.5 text-right">R$ / mês</th>
                </tr>
              </thead>
              <tbody>
                {(data.recorrentes?.itens || []).map((r, i) => (
                  <tr key={i} className="border-b border-tertiary last:border-0 hover:bg-stone-50">
                    <td className="px-4 py-2.5">{r.nome}</td>
                    <td className="px-4 py-2.5 text-ink-500 capitalize">{r.categoria}</td>
                    <td className="px-4 py-2.5 font-mono text-right">{fmtBRL(r.valor_mes)}</td>
                  </tr>
                ))}
                {(data.recorrentes?.itens || []).length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-ink-500">Nenhuma recorrente ativa cadastrada</td></tr>
                )}
              </tbody>
              <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold">
                <tr>
                  <td className="px-4 py-3" colSpan={2}>TOTAL / mês</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtBRL(data.recorrentes?.valor_mes)}</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </div>

        <Card className="p-5">
          <div className="font-bold text-ink-900 text-sm uppercase tracking-wider mb-4">Como o Custo por Saco é calculado</div>
          <div className="space-y-3 text-sm text-ink-700">
            <div className="flex justify-between border-b border-tertiary pb-2">
              <span>Recorrentes do período</span>
              <span className="font-mono font-semibold">{fmtBRL(data.recorrentes?.valor_periodo)}</span>
            </div>
            <div className="flex justify-between border-b border-tertiary pb-2">
              <span>+ Folha do período</span>
              <span className="font-mono font-semibold">{fmtBRL(data.folha?.valor_periodo)}</span>
            </div>
            {custoTerceirizado > 0 && (
              <div className="border-b border-tertiary pb-2">
                <div className="flex justify-between">
                  <span>+ Terceirização (R$/saco fixo)</span>
                  <span className="font-mono font-semibold" data-testid="cps-custo-terceirizado">{fmtBRL(custoTerceirizado)}</span>
                </div>
                {custoTerceirizadoDetalhe.map((x) => (
                  <div key={x.unidade} className="flex justify-between text-xs text-ink-500 pl-4">
                    <span>• {x.unidade}: {fmtNum(x.sacos, 0)} × R$ {x.preco.toFixed(2)}/sc</span>
                    <span className="font-mono">{fmtBRL(x.total)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between border-b-2 border-ink-900 pb-2 pt-1">
              <span className="font-bold">= Custo total</span>
              <span className="font-mono font-bold">{fmtBRL(totalCusto)}</span>
            </div>
            <div className="space-y-1.5 border-b border-tertiary pb-2">
              <div className="flex justify-between">
                <span>÷ Sacos beneficiados (total)</span>
                <span className="font-mono font-semibold" data-testid="cps-sacos-total">{fmtNum(sacos, 0)}</span>
              </div>
              <div className="flex justify-between text-xs text-ink-500 pl-4">
                <span>• próprios</span>
                <span className="font-mono" data-testid="cps-sacos-proprios">{fmtNum(data.producao?.sacos_proprios || 0, 0)}</span>
              </div>
              <div className="flex justify-between text-xs text-ink-500 pl-4">
                <span>• terceiros (maquinação para terceiros)</span>
                <span className="font-mono" data-testid="cps-sacos-terceiros">{fmtNum(data.producao?.sacos_terceiros || 0, 0)}</span>
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <span className="font-bold text-emerald-700">= Custo por saco</span>
              <span className="font-mono font-bold text-emerald-700 text-lg">{fmtBRL(cps)}</span>
            </div>
          </div>
          <div className="mt-4 text-xs text-ink-500 bg-stone-50 rounded p-3 border border-stone-200">
            <b>Observação:</b> se ainda não houver folha fechada no período, o sistema usa o
            salário base + complemento mensal dos funcionários ativos como estimativa.
          </div>
        </Card>
      </div>

      {/* Produção diária: contagem de sacos beneficiados por dia */}
      <Card className="mt-6 overflow-hidden" data-testid="cps-producao-diaria-card">
        <div className="px-5 py-4 border-b border-tertiary flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-bold text-ink-900 text-sm uppercase tracking-wider">Produção Diária</div>
            <div className="text-xs text-ink-500 mt-0.5">
              Sacos beneficiados por dia (data do beneficiamento) — próprios vs. terceiros
            </div>
          </div>
          <div className="text-xs text-ink-500 text-right">
            {(data.producao?.n_dias_producao || 0)} dia(s) com produção • rateio diário: <b className="font-mono">{fmtBRL(data.producao?.custo_alocado_por_dia || 0)}</b>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-tertiary">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-2.5">Data</th>
                <th className="px-4 py-2.5 text-right">Próprios</th>
                <th className="px-4 py-2.5 text-right">Terceiros</th>
                <th className="px-4 py-2.5 text-right">Total sacos</th>
                <th className="px-4 py-2.5 text-right">NFs</th>
                <th className="px-4 py-2.5 text-right">Custo/saco do dia</th>
              </tr>
            </thead>
            <tbody>
              {(data.producao?.diaria || []).map((d) => (
                <tr
                  key={d.data}
                  className="border-b border-tertiary last:border-0 hover:bg-brand-50 cursor-pointer transition-colors"
                  data-testid={`cps-diaria-row-${d.data}`}
                  onClick={async () => {
                    setDrillLoading(true);
                    setDrillDay({ data: d.data, loading: true });
                    try {
                      const r = await api.get(`/reports/producao-dia?data=${d.data}`);
                      setDrillDay(r.data);
                    } catch (e) {
                      alert("Erro ao carregar detalhes: " + (e.response?.data?.detail || e.message));
                      setDrillDay(null);
                    } finally { setDrillLoading(false); }
                  }}
                  title="Clique para ver as cargas deste dia"
                >
                  <td className="px-4 py-2.5 font-semibold text-ink-900">
                    {(() => { try { const [y,m,day] = d.data.split("-"); return `${day}/${m}/${y}`; } catch { return d.data; } })()}
                    <span className="ml-1.5 text-[10px] text-brand-600 font-normal">↗</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-right">{fmtNum(d.sacos_proprios, 0)}</td>
                  <td className="px-4 py-2.5 font-mono text-right text-purple-700">{fmtNum(d.sacos_terceiros, 0)}</td>
                  <td className="px-4 py-2.5 font-mono text-right font-bold">{fmtNum(d.sacos_total, 0)}</td>
                  <td className="px-4 py-2.5 font-mono text-right text-ink-500">
                    {d.nfs_proprias + d.nfs_terceiros}
                    {d.nfs_terceiros > 0 && <span className="text-purple-700"> ({d.nfs_terceiros}t)</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-right text-emerald-700 font-semibold">{fmtBRL(d.custo_por_saco)}</td>
                </tr>
              ))}
              {(data.producao?.diaria || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-500">Nenhum beneficiamento registrado no período</td></tr>
              )}
            </tbody>
            {(data.producao?.diaria || []).length > 0 && (
              <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold">
                <tr>
                  <td className="px-4 py-3">TOTAL</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtNum(data.producao?.sacos_proprios || 0, 0)}</td>
                  <td className="px-4 py-3 font-mono text-right text-purple-700">{fmtNum(data.producao?.sacos_terceiros || 0, 0)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtNum(data.producao?.sacos_beneficiados || 0, 0)}</td>
                  <td className="px-4 py-3 font-mono text-right text-ink-500">
                    {(data.producao?.nfs_beneficiadas || 0)}
                    {(data.producao?.nfs_terceiros || 0) > 0 && <span className="text-purple-700"> ({data.producao.nfs_terceiros}t)</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-right text-emerald-700">{fmtBRL(cps)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-5 py-3 text-xs text-ink-500 bg-stone-50 border-t border-tertiary">
          <b>Como o custo/saco do dia é calculado:</b> o custo fixo do período (recorrentes + folha) é dividido igualmente pelos dias com beneficiamento (rateio diário), e esse valor é dividido pelos sacos do dia. Dias com muitos sacos ficam com custo/saco menor; dias com pouca produção ficam mais caros. <b>Terceiros</b> em roxo — o volume aparece porque o beneficiamento consome estrutura, mas você pode considerar apenas a coluna Próprios ao avaliar produtividade interna.
          <br /><b>Clique em qualquer linha</b> para ver quais cargas compõem o total daquele dia.
        </div>
      </Card>

      {/* Modal drill-down: cargas do dia */}
      {drillDay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDrillDay(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-tertiary flex items-center justify-between bg-stone-50">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Cargas beneficiadas</div>
                <div className="text-lg font-heading font-bold text-ink-900">
                  {(() => { try { const [y,m,day] = drillDay.data.split("-"); return `${day}/${m}/${y}`; } catch { return drillDay.data; } })()}
                </div>
              </div>
              <button onClick={() => setDrillDay(null)} className="text-ink-500 hover:text-ink-900 text-2xl leading-none" data-testid="drill-close">×</button>
            </div>
            {drillLoading ? (
              <div className="flex-1 flex items-center justify-center p-10 text-ink-500">Carregando…</div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Totais */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Próprios</div>
                    <div className="font-mono text-xl font-bold text-emerald-900">{fmtNum(drillDay.totais?.sacos_proprios || 0, 0)}</div>
                    <div className="text-[10px] text-emerald-700">{drillDay.totais?.nfs_proprias || 0} NF(s)</div>
                  </div>
                  <div className="p-3 rounded-md bg-purple-50 border border-purple-200">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Terceiros</div>
                    <div className="font-mono text-xl font-bold text-purple-900">{fmtNum(drillDay.totais?.sacos_terceiros || 0, 0)}</div>
                    <div className="text-[10px] text-purple-700">{drillDay.totais?.nfs_terceiros || 0} NF(s)</div>
                  </div>
                  <div className="p-3 rounded-md bg-ink-200/40 border border-ink-200">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Total do dia</div>
                    <div className="font-mono text-xl font-bold text-ink-900">{fmtNum(drillDay.totais?.sacos_total || 0, 0)}</div>
                    <div className="text-[10px] text-ink-500">sacos beneficiados</div>
                  </div>
                </div>

                {/* Próprios */}
                {(drillDay.proprios || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Beneficiamento Próprio</div>
                    <div className="border border-tertiary rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-tertiary">
                          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-ink-500">
                            <th className="px-3 py-2">NF</th>
                            <th className="px-3 py-2">Romaneio</th>
                            <th className="px-3 py-2">Fornecedor</th>
                            <th className="px-3 py-2">Unidade</th>
                            <th className="px-3 py-2 text-right">Sacos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillDay.proprios.map((r) => (
                            <tr key={r.invoice_id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`drill-prop-${r.invoice_id}`}>
                              <td className="px-3 py-2 font-semibold text-ink-900">#{r.nota_numero || "—"}</td>
                              <td className="px-3 py-2 text-ink-600 text-xs">{r.romaneio_numero || "—"}</td>
                              <td className="px-3 py-2 text-ink-700">{r.fornecedor || "—"}</td>
                              <td className="px-3 py-2 text-ink-600 text-xs">{r.unidade || "—"}</td>
                              <td className="px-3 py-2 font-mono text-right font-semibold">{fmtNum(r.sacos, 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-emerald-50 font-bold border-t border-emerald-200">
                          <tr>
                            <td colSpan={4} className="px-3 py-2 text-emerald-800">SUBTOTAL PRÓPRIOS</td>
                            <td className="px-3 py-2 font-mono text-right text-emerald-900">{fmtNum(drillDay.totais?.sacos_proprios || 0, 0)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Terceiros */}
                {(drillDay.terceiros || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Beneficiamento para Terceiros</div>
                    <div className="border border-tertiary rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-tertiary">
                          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-ink-500">
                            <th className="px-3 py-2">NF</th>
                            <th className="px-3 py-2">Romaneio</th>
                            <th className="px-3 py-2">Cliente</th>
                            <th className="px-3 py-2">Unidade</th>
                            <th className="px-3 py-2 text-right">Sacos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillDay.terceiros.map((r) => (
                            <tr key={r.invoice_id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`drill-terc-${r.invoice_id}`}>
                              <td className="px-3 py-2 font-semibold text-ink-900">#{r.nota_numero || "—"}</td>
                              <td className="px-3 py-2 text-ink-600 text-xs">{r.romaneio_numero || "—"}</td>
                              <td className="px-3 py-2 text-ink-700">{r.fornecedor || "—"}</td>
                              <td className="px-3 py-2 text-ink-600 text-xs">{r.unidade || "—"}</td>
                              <td className="px-3 py-2 font-mono text-right font-semibold text-purple-700">{fmtNum(r.sacos, 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-purple-50 font-bold border-t border-purple-200">
                          <tr>
                            <td colSpan={4} className="px-3 py-2 text-purple-800">SUBTOTAL TERCEIROS</td>
                            <td className="px-3 py-2 font-mono text-right text-purple-900">{fmtNum(drillDay.totais?.sacos_terceiros || 0, 0)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {(drillDay.proprios || []).length === 0 && (drillDay.terceiros || []).length === 0 && (
                  <div className="text-center py-8 text-ink-500">Nenhuma carga encontrada nessa data.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sacos Maquinados por Unidade
// ============================================================================
const CAT_LABEL = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };
const CAT_COLOR = {
  cx1: { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534" },
  cx2: { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF" },
  cx3: { bg: "#FEF9C3", border: "#FDE68A", text: "#92400E" },
  cx3g: { bg: "#FFF7ED", border: "#FED7AA", text: "#9A3412" },
  cx4: { bg: "#FAF5FF", border: "#E9D5FF", text: "#6B21A8" },
};

function SacosMaquinadosView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    setErr("");
    api
      .get("/reports/sacos-maquinados", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <Spinner />;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;
  if (!data) return null;

  const cats = data.categorias || ["cx1", "cx2", "cx3", "cx3g", "cx4"];

  return (
    <div className="space-y-6" data-testid="sacos-maquinados-view">
      {/* Cards totais gerais por CX */}
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-3">
          Total geral · {data.total.nfs} NF(s) beneficiada(s) no período
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {cats.map((c) => {
            const co = CAT_COLOR[c] || CAT_COLOR.cx1;
            return (
              <div
                key={c}
                className="rounded-xl p-4 border"
                style={{ background: co.bg, borderColor: co.border }}
                data-testid={`sm-total-${c}`}
              >
                <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: co.text }}>
                  {CAT_LABEL[c]}
                </div>
                <div className="font-mono text-3xl font-extrabold mt-1" style={{ color: co.text }}>
                  {fmtNum(data.total[c] || 0, 0)}
                </div>
                <div className="text-[11px]" style={{ color: co.text }}>sacos</div>
              </div>
            );
          })}
          <div className="rounded-xl p-4 border bg-ink-900 border-ink-900" data-testid="sm-total-geral">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">Total</div>
            <div className="font-mono text-3xl font-extrabold mt-1 text-white">
              {fmtNum(data.total.total || 0, 0)}
            </div>
            <div className="text-[11px] text-white/70">sacos</div>
          </div>
        </div>
      </div>

      {/* Por Unidade */}
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Contagem por unidade
        </div>
        {(data.por_unidade || []).map((u) => (
          <Card key={u.unidade} className="overflow-hidden" data-testid={`sm-unidade-${u.unidade}`}>
            <div className="p-4 flex items-center justify-between border-b border-ink-200 bg-brand-50/40">
              <div>
                <div className="font-semibold text-ink-900 text-base">{u.unidade}</div>
                <div className="text-[11px] text-ink-500">{u.nfs} NF(s) beneficiada(s)</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-ink-500">Total unidade</div>
                <div className="font-mono text-2xl font-extrabold text-brand-700">
                  {fmtNum(u.total || 0, 0)}
                </div>
                <div className="text-[10px] text-ink-500">sacos</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
              {cats.map((c) => {
                const co = CAT_COLOR[c] || CAT_COLOR.cx1;
                const total = u.total || 0;
                const val = u[c] || 0;
                const pct = total > 0 ? (val / total) * 100 : 0;
                return (
                  <div
                    key={c}
                    className="rounded-lg p-3 border"
                    style={{ background: co.bg, borderColor: co.border }}
                    data-testid={`sm-${u.unidade}-${c}`}
                  >
                    <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: co.text }}>
                      {CAT_LABEL[c]}
                    </div>
                    <div className="font-mono text-2xl font-bold mt-1" style={{ color: co.text }}>
                      {fmtNum(val, 0)}
                    </div>
                    <div className="text-[10px]" style={{ color: co.text }}>
                      {pct.toFixed(1)}% · sacos
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
        {(data.por_unidade || []).length === 0 && (
          <div className="text-sm text-ink-500 p-6 text-center">
            Nenhuma NF beneficiada no período.
          </div>
        )}
      </div>
    </div>
  );
}



// ============================================================================
// Saídas por Unidade (Expedições finalizadas)
// ============================================================================
function SaidasPorUnidadeView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    setErr("");
    api
      .get("/reports/saidas-por-unidade", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <Spinner />;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;
  if (!data) return null;

  const cats = data.categorias || ["cx1", "cx2", "cx3", "cx3g", "cx4"];

  return (
    <div className="space-y-6" data-testid="saidas-unidade-view">
      {/* Cards totais gerais por CX */}
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-3">
          Total geral · {data.total.n_shipments} expedição(ões) finalizada(s) no período
          {data.total.granel_kg > 0 && (
            <span className="ml-2 text-ink-700">
              · Granel: <span className="font-mono">{fmtNum(data.total.granel_kg, 0)} kg</span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {cats.map((c) => {
            const co = CAT_COLOR[c] || CAT_COLOR.cx1;
            return (
              <div
                key={c}
                className="rounded-xl p-4 border"
                style={{ background: co.bg, borderColor: co.border }}
                data-testid={`sd-total-${c}`}
              >
                <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: co.text }}>
                  {CAT_LABEL[c]}
                </div>
                <div className="font-mono text-3xl font-extrabold mt-1" style={{ color: co.text }}>
                  {fmtNum(data.total[c] || 0, 0)}
                </div>
                <div className="text-[11px]" style={{ color: co.text }}>sacos</div>
              </div>
            );
          })}
          <div className="rounded-xl p-4 border bg-ink-900 border-ink-900" data-testid="sd-total-geral">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">Total</div>
            <div className="font-mono text-3xl font-extrabold mt-1 text-white">
              {fmtNum(data.total.total || 0, 0)}
            </div>
            <div className="text-[11px] text-white/70">sacos</div>
          </div>
        </div>
      </div>

      {/* Por Unidade */}
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Saídas por unidade de origem (NF)
        </div>
        {(data.por_unidade || []).map((u) => (
          <Card key={u.unidade} className="overflow-hidden" data-testid={`sd-unidade-${u.unidade}`}>
            <div className="p-4 flex items-center justify-between border-b border-ink-200 bg-brand-50/40">
              <div>
                <div className="font-semibold text-ink-900 text-base">{u.unidade}</div>
                <div className="text-[11px] text-ink-500">
                  {u.n_shipments} expedição(ões)
                  {u.granel_kg > 0 && (
                    <span> · Granel: <span className="font-mono">{fmtNum(u.granel_kg, 0)} kg</span></span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-ink-500">Total unidade</div>
                <div className="font-mono text-2xl font-extrabold text-brand-700">
                  {fmtNum(u.total || 0, 0)}
                </div>
                <div className="text-[10px] text-ink-500">sacos</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
              {cats.map((c) => {
                const co = CAT_COLOR[c] || CAT_COLOR.cx1;
                const total = u.total || 0;
                const val = u[c] || 0;
                const pct = total > 0 ? (val / total) * 100 : 0;
                return (
                  <div
                    key={c}
                    className="rounded-lg p-3 border"
                    style={{ background: co.bg, borderColor: co.border }}
                    data-testid={`sd-${u.unidade}-${c}`}
                  >
                    <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: co.text }}>
                      {CAT_LABEL[c]}
                    </div>
                    <div className="font-mono text-2xl font-bold mt-1" style={{ color: co.text }}>
                      {fmtNum(val, 0)}
                    </div>
                    <div className="text-[10px]" style={{ color: co.text }}>
                      {pct.toFixed(1)}% · sacos
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
        {(data.por_unidade || []).length === 0 && (
          <div className="text-sm text-ink-500 p-6 text-center">
            Nenhuma expedição finalizada no período.
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// P&L por Nota de Venda (Shipment/Saída)
// ============================================================================
function PnlByShipmentView({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [orderBy, setOrderBy] = useState("data"); // data | lucro | margem | receita
  const [orderDir, setOrderDir] = useState("desc");
  const [drillId, setDrillId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr("");
    api
      .get("/reports/pnl/by-shipment", { params: { from_: from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <Spinner />;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;
  if (!data) return null;

  const setSort = (col) => {
    if (orderBy === col) setOrderDir(orderDir === "desc" ? "asc" : "desc");
    else { setOrderBy(col); setOrderDir("desc"); }
  };
  const rows = [...(data.rows || [])].sort((a, b) => {
    const key = orderBy === "margem" ? "margem_pct" : orderBy;
    const va = a[key] ?? 0, vb = b[key] ?? 0;
    if (orderBy === "data") return orderDir === "desc" ? (vb > va ? 1 : -1) : (va > vb ? 1 : -1);
    return orderDir === "desc" ? vb - va : va - vb;
  });

  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="pnl-by-shipment-view">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Package} label="Saídas" value={fmtNum(t.n_shipments || 0, 0)} />
        <KpiCard icon={DollarSign} label="Receita bruta" value={fmtBRL(t.receita_bruta || 0)} />
        <KpiCard icon={TrendingDown} label="Deduções" value={fmtBRL(t.deducoes || 0)} tone="warn" />
        <KpiCard icon={DollarSign} label="Receita líquida" value={fmtBRL(t.receita || 0)} />
        <KpiCard icon={TrendingDown} label="CMV total" value={fmtBRL(t.cmv || 0)} tone="danger" />
        <KpiCard icon={TrendingUp} label="Lucro" value={fmtBRL(t.lucro || 0)} hint={`Margem ${fmtNum(t.margem_pct || 0, 2)}%`} tone={t.lucro >= 0 ? "ok" : "danger"} />
      </div>

      {/* Tabela */}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-ink-200">
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
              <th className="px-3 py-2 cursor-pointer" onClick={() => setSort("data")}>Data</th>
              <th className="px-3 py-2">Nota</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Unidade</th>
              <th className="px-3 py-2 text-right">Caixas</th>
              <th className="px-3 py-2 text-right cursor-pointer" onClick={() => setSort("receita")}>Rec. Bruta</th>
              <th className="px-3 py-2 text-right">Deduções</th>
              <th className="px-3 py-2 text-right">Rec. Líq.</th>
              <th className="px-3 py-2 text-right">CMV</th>
              <th className="px-3 py-2 text-right cursor-pointer" onClick={() => setSort("lucro")}>Lucro</th>
              <th className="px-3 py-2 text-right cursor-pointer" onClick={() => setSort("margem")}>Margem</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const neg = r.lucro < 0;
              return (
                <tr key={r.shipment_id} onClick={() => setDrillId(r.shipment_id)} className={`border-b border-ink-100 last:border-0 hover:bg-brand-50/40 cursor-pointer ${neg ? "bg-red-50/40" : ""}`} data-testid={`shipment-row-${r.shipment_id}`} title="Clique para ver detalhes por categoria">
                  <td className="px-3 py-1.5 font-mono text-[12px] text-ink-600 whitespace-nowrap">{fmtDateBR(r.data)}</td>
                  <td className="px-3 py-1.5 font-mono text-[12px] text-brand-700 underline decoration-dotted">{r.numero}</td>
                  <td className="px-3 py-1.5 text-ink-800 max-w-[240px] truncate" title={r.cliente}>{r.cliente}</td>
                  <td className="px-3 py-1.5 text-[12px] text-ink-600">{r.unidade || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtNum(r.total_caixas || 0, 0)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtBRL(r.receita_bruta)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-amber-700">{fmtBRL(r.deducoes)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtBRL(r.receita)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-red-700">{fmtBRL(r.cmv)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono font-bold ${neg ? "text-danger" : "text-ok"}`}>{fmtBRL(r.lucro)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono font-semibold ${neg ? "text-danger" : "text-ok"}`}>{fmtNum(r.margem_pct || 0, 1)}%</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-ink-500">Nenhuma saída finalizada no período.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold">
              <tr>
                <td className="px-3 py-2" colSpan={5}>TOTAL — {rows.length} saída(s)</td>
                <td className="px-3 py-2 text-right font-mono">{fmtBRL(t.receita_bruta)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtBRL(t.deducoes)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtBRL(t.receita)}</td>
                <td className="px-3 py-2 text-right font-mono text-red-700">{fmtBRL(t.cmv)}</td>
                <td className={`px-3 py-2 text-right font-mono ${t.lucro < 0 ? "text-danger" : "text-ok"}`}>{fmtBRL(t.lucro)}</td>
                <td className={`px-3 py-2 text-right font-mono ${t.lucro < 0 ? "text-danger" : "text-ok"}`}>{fmtNum(t.margem_pct || 0, 2)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      {drillId && <ShipmentPnlDetailModal shipmentId={drillId} onClose={() => setDrillId(null)} />}
    </div>
  );
}

function ShipmentPnlDetailModal({ shipmentId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setErr("");
    api.get(`/reports/pnl/by-shipment/${shipmentId}/detail`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [shipmentId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="shipment-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-500 font-bold">Detalhes da Saída</div>
            <div className="font-heading text-lg font-bold text-ink-900">
              {d ? `Nota ${d.numero} · ${d.cliente}` : "Carregando..."}
            </div>
            {d && (
              <div className="text-xs text-ink-500 mt-0.5">
                {fmtDateBR(d.data)}
                {d.motorista && <> · Motorista: {d.motorista}</>}
                {d.vendedor && <> · Vendedor: {d.vendedor}</>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100 text-ink-500" data-testid="shipment-detail-close">✕</button>
        </div>

        {loading && <div className="p-8 flex justify-center"><Spinner /></div>}
        {err && <div className="p-4 text-sm text-red-700 bg-red-50">{err}</div>}
        {d && (
          <div className="p-6 space-y-6">
            {/* Header financial summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <MiniStat label="Rec. Bruta" value={fmtBRL(d.receita_bruta)} />
              <MiniStat label="Deduções" value={fmtBRL(d.deducoes)} tone="warn" />
              <MiniStat label="Rec. Líquida" value={fmtBRL(d.receita)} />
              <MiniStat label="CMV" value={fmtBRL(d.cmv)} tone="danger" />
              <MiniStat label="Lucro" value={fmtBRL(d.lucro)} tone={d.lucro >= 0 ? "ok" : "danger"} />
              <MiniStat label="Margem" value={`${fmtNum(d.margem_pct, 2)}%`} tone={d.lucro >= 0 ? "ok" : "danger"} />
            </div>

            {/* Deduções breakdown */}
            {d.deducoes > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                <div className="text-[11px] uppercase tracking-wider font-bold text-amber-900 mb-1">Composição das deduções</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                  <div><div className="text-ink-500">Desconto</div><div className="font-mono font-semibold">{fmtBRL(d.deducoes_breakdown.desconto)}</div></div>
                  <div><div className="text-ink-500">Frete CIF</div><div className="font-mono font-semibold">{fmtBRL(d.deducoes_breakdown.frete_cif)}</div></div>
                  <div><div className="text-ink-500">Descarga</div><div className="font-mono font-semibold">{fmtBRL(d.deducoes_breakdown.descarga)}</div></div>
                  <div><div className="text-ink-500">Seguro</div><div className="font-mono font-semibold">{fmtBRL(d.deducoes_breakdown.seguro)}</div></div>
                  <div><div className="text-ink-500">Comissão</div><div className="font-mono font-semibold">{fmtBRL(d.deducoes_breakdown.comissao)}</div></div>
                </div>
              </div>
            )}

            {/* Tabela por categoria */}
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">Lucro por categoria (CX1..CX4/CX3G)</div>
              <div className="overflow-x-auto border border-ink-200 rounded-md">
                <table className="w-full text-sm" data-testid="shipment-detail-cats-table">
                  <thead className="bg-stone-50 border-b border-ink-200">
                    <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-500">
                      <th className="px-3 py-2">Categoria</th>
                      <th className="px-3 py-2 text-right">Qtd (sc)</th>
                      <th className="px-3 py-2 text-right">Venda/sc</th>
                      <th className="px-3 py-2 text-right">Custo/sc</th>
                      <th className="px-3 py-2 text-right">Rec. Bruta</th>
                      <th className="px-3 py-2 text-right">Rec. Líq.</th>
                      <th className="px-3 py-2 text-right">CMV</th>
                      <th className="px-3 py-2 text-right">Lucro</th>
                      <th className="px-3 py-2 text-right">Margem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.categorias.filter((c) => c.quantidade > 0).map((c) => {
                      const co = CAT_COLOR[c.categoria.toLowerCase()] || CAT_COLOR.cx1;
                      const neg = c.lucro < 0;
                      return (
                        <tr key={c.categoria} className={`border-b border-ink-100 last:border-0 ${neg ? "bg-red-50/50" : ""}`} data-testid={`cat-row-${c.categoria}`}>
                          <td className="px-3 py-2">
                            <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold" style={{ background: co.bg, color: co.text, border: `1px solid ${co.border}` }}>
                              {c.categoria}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{fmtNum(c.quantidade, 0)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtBRL(c.preco_venda_medio)}</td>
                          <td className="px-3 py-2 text-right font-mono text-red-700">{fmtBRL(c.preco_custo_medio)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtBRL(c.receita_bruta)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtBRL(c.receita)}</td>
                          <td className="px-3 py-2 text-right font-mono text-red-700">{fmtBRL(c.cmv)}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${neg ? "text-danger" : "text-ok"}`}>{fmtBRL(c.lucro)}</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${neg ? "text-danger" : "text-ok"}`}>{fmtNum(c.margem_pct, 2)}%</td>
                        </tr>
                      );
                    })}
                    {d.categorias.filter((c) => c.quantidade > 0).length === 0 && (
                      <tr><td colSpan={9} className="px-4 py-6 text-center text-ink-500 text-xs">Sem quebra por categoria (venda a granel).</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-ink-500 mt-2">
                <strong>Venda/sc</strong>: preço médio praticado na nota. <strong>Custo/sc</strong>: custo real (Boca-de-Máquina + rateio de chapa/ICMS/frete/maquinação/sacaria).
              </div>
            </div>

            {/* NFs de origem */}
            {d.nfs?.length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">NFs de compra que originaram a mercadoria</div>
                <div className="overflow-x-auto border border-ink-200 rounded-md">
                  <table className="w-full text-sm" data-testid="shipment-detail-nfs-table">
                    <thead className="bg-stone-50 border-b border-ink-200">
                      <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-500">
                        <th className="px-3 py-2">NF</th>
                        <th className="px-3 py-2">Fornecedor</th>
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2 text-right">Sacos</th>
                        <th className="px-3 py-2 text-right">Rec. Líq.</th>
                        <th className="px-3 py-2 text-right">CMV</th>
                        <th className="px-3 py-2 text-right">Lucro</th>
                        <th className="px-3 py-2 text-right">Margem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.nfs.map((n) => {
                        const neg = n.lucro < 0;
                        return (
                          <tr key={n.invoice_id} className={`border-b border-ink-100 last:border-0 ${neg ? "bg-red-50/50" : ""}`}>
                            <td className="px-3 py-2 font-mono text-[12px]">#{n.nota_numero || "—"}</td>
                            <td className="px-3 py-2 text-ink-800">{n.fornecedor}</td>
                            <td className="px-3 py-2 text-[12px] capitalize text-ink-600">{n.tipo_cebola}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtNum(n.quantidade, 0)}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtBRL(n.receita)}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-700">{fmtBRL(n.cmv)}</td>
                            <td className={`px-3 py-2 text-right font-mono font-bold ${neg ? "text-danger" : "text-ok"}`}>{fmtBRL(n.lucro)}</td>
                            <td className={`px-3 py-2 text-right font-mono font-semibold ${neg ? "text-danger" : "text-ok"}`}>{fmtNum(n.margem_pct, 2)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "default" }) {
  const tones = {
    default: "text-ink-900",
    ok: "text-ok",
    danger: "text-danger",
    warn: "text-amber-700",
  };
  return (
    <div className="rounded-md bg-stone-50 border border-ink-200 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">{label}</div>
      <div className={`font-mono text-lg font-bold ${tones[tone] || tones.default} mt-0.5`}>{value}</div>
    </div>
  );
}

function fmtDateBR(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}


// ============================================================================
// Contas Financeiras por Categoria
// ============================================================================
const CAT_FIN_LABELS = {
  fornecedor: "Fornecedores / Produtores",
  salario: "Salário",
  salario_adm: "Salários ADM",
  folha: "Folha",
  impostos: "Impostos",
  frete: "Frete",
  sacaria: "Sacaria",
  energia: "Energia",
  aluguel: "Aluguel",
  manutencao: "Manutenção",
  materiais_escritorio: "Mat. Escritório",
  materiais_limpeza: "Mat. Limpeza",
  taxas_bancarias: "Taxas Bancárias",
  cesta_basica: "Cesta Básica",
  embalagem: "Embalagem",
  outros: "Outros",
  venda: "Vendas",
  servico_beneficiamento: "Benef. Terceiros",
};
const CAT_FIN_COLOR = {
  fornecedor: "#059669", salario: "#0284C7", salario_adm: "#0891B2", folha: "#0284C7",
  impostos: "#DC2626", frete: "#2563EB", sacaria: "#EA580C", energia: "#CA8A04",
  aluguel: "#4F46E5", manutencao: "#E11D48", materiais_escritorio: "#64748B",
  materiais_limpeza: "#0EA5E9", taxas_bancarias: "#7C3AED", cesta_basica: "#84CC16",
  embalagem: "#D97706", outros: "#78716C", venda: "#F59E0B", servico_beneficiamento: "#A855F7",
};
const fmtCatLabel = (c) => CAT_FIN_LABELS[c] || c;

function ByCategoryView({ from, to }) {
  const [kind, setKind] = useState("payable"); // payable | receivable
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [data, setData] = useState(null);
  const [custoOp, setCustoOp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [drill, setDrill] = useState(null); // categoria selecionada

  useEffect(() => {
    setLoading(true); setErr("");
    api.get("/reports/finance/by-category", { params: { kind, from_: from, to, only_open: onlyOpen } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [kind, from, to, onlyOpen]);

  useEffect(() => {
    // Só busca custo operacional se estiver na aba "Contas a Pagar"
    if (kind !== "payable") { setCustoOp(null); return; }
    api.get("/reports/finance/custo-operacional-por-saco", { params: { from_: from, to } })
      .then((r) => setCustoOp(r.data))
      .catch(() => setCustoOp(null));
  }, [kind, from, to]);

  if (loading) return <Spinner />;
  if (err) return <div className="text-red-700 text-sm">{err}</div>;
  if (!data) return null;

  const t = data.totais || {};
  const rows = data.rows || [];
  const chartData = rows.slice(0, 10).map((r) => ({
    label: fmtCatLabel(r.categoria).slice(0, 20),
    valor: r.valor_total,
    saldo: r.saldo,
    fill: CAT_FIN_COLOR[r.categoria] || "#78716C",
  }));

  return (
    <div className="space-y-6" data-testid="by-category-view">
      {/* Toggle e KPIs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="inline-flex rounded-md bg-stone-100 p-1 print:hidden">
          <button
            onClick={() => setKind("payable")}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${kind === "payable" ? "bg-white text-danger shadow-sm" : "text-ink-500"}`}
            data-testid="bycat-payable"
          >
            Contas a Pagar
          </button>
          <button
            onClick={() => setKind("receivable")}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${kind === "receivable" ? "bg-white text-emerald-700 shadow-sm" : "text-ink-500"}`}
            data-testid="bycat-receivable"
          >
            Contas a Receber
          </button>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-ink-700 print:hidden">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} data-testid="bycat-only-open" />
          <span>Apenas em aberto</span>
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={Layers} label="Categorias" value={fmtNum(rows.length, 0)} />
        <KpiCard icon={FileText} label="Contas" value={fmtNum(t.n_contas || 0, 0)} />
        <KpiCard icon={DollarSign} label="Valor total" value={fmtBRL(t.valor_total || 0)} />
        <KpiCard icon={DollarSign} label="Já pago" value={fmtBRL(t.total_pago || 0)} tone="ok" />
        <KpiCard icon={DollarSign} label="Saldo" value={fmtBRL(t.saldo || 0)} tone={kind === "payable" ? "danger" : "default"} hint={t.vencidas > 0 ? `${t.vencidas} venc · ${fmtBRL(t.vencido_valor)}` : undefined} />
      </div>

      {/* Custo Operacional por Saco */}
      {kind === "payable" && custoOp && (
        <Card className="p-5 bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200" data-testid="custo-op-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-amber-800">💡 Custo Operacional por Saco Maquinado</div>
              <div className="text-xs text-ink-600 mt-0.5">
                Soma de Salário, Salários ADM, Aluguel, Manutenção, Folha, Impostos, Energia, Mat. Escritório e Mat. Limpeza · dividido pelos sacos beneficiados no período
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">Custo total</div>
                <div className="font-mono text-xl font-bold text-danger">{fmtBRL(custoOp.custo_total)}</div>
              </div>
              <div className="text-2xl text-ink-300">÷</div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">Sacos maquinados</div>
                <div className="font-mono text-xl font-bold text-ink-900">{fmtNum(custoOp.sacos_total, 0)}</div>
              </div>
              <div className="text-2xl text-ink-300">=</div>
              <div className="bg-amber-100 border border-amber-300 rounded-lg px-4 py-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">R$/saco</div>
                <div className="font-mono text-2xl font-extrabold text-amber-900" data-testid="custo-op-por-saco">
                  {fmtBRL(custoOp.custo_por_saco || 0)}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Gráfico */}
      {chartData.length > 0 && (
        <Card className="p-4 print:hidden">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3">Distribuição por categoria (top 10)</div>
          <div className="h-64 min-h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 100, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} stroke="#5E5E5E" />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} stroke="#5E5E5E" />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E2DC", fontSize: 12 }} formatter={(v) => fmtBRL(v)} />
                <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
                  {chartData.map((d, i) => (<Cell key={i} fill={d.fill} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Tabela */}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="bycat-table">
          <thead className="bg-stone-50 border-b border-ink-200">
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
              <th className="px-4 py-2">Categoria</th>
              <th className="px-4 py-2 text-right">Contas</th>
              <th className="px-4 py-2 text-right">Valor Total</th>
              <th className="px-4 py-2 text-right">Pago</th>
              <th className="px-4 py-2 text-right">Saldo</th>
              <th className="px-4 py-2 text-right">Vencidas</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.categoria} onClick={() => setDrill(r.categoria)} className="border-b border-ink-100 last:border-0 hover:bg-brand-50/40 cursor-pointer" data-testid={`bycat-row-${r.categoria}`}>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: CAT_FIN_COLOR[r.categoria] || "#78716C" }} />
                    <span className="font-semibold text-ink-900">{fmtCatLabel(r.categoria)}</span>
                  </span>
                </td>
                <td className="px-4 py-2 text-right font-mono">{fmtNum(r.n_contas, 0)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtBRL(r.valor_total)}</td>
                <td className="px-4 py-2 text-right font-mono text-emerald-700">{fmtBRL(r.total_pago)}</td>
                <td className="px-4 py-2 text-right font-mono font-bold text-ink-900">{fmtBRL(r.saldo)}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {r.vencidas > 0 ? <span className="text-danger">{r.vencidas} · {fmtBRL(r.vencido_valor)}</span> : <span className="text-ink-400">—</span>}
                </td>
                <td className="px-4 py-2 text-right text-brand-600 text-[11px] print:hidden">Ver →</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-500">Nenhuma conta no período.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold">
              <tr>
                <td className="px-4 py-2">TOTAL</td>
                <td className="px-4 py-2 text-right font-mono">{fmtNum(t.n_contas, 0)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtBRL(t.valor_total)}</td>
                <td className="px-4 py-2 text-right font-mono text-emerald-700">{fmtBRL(t.total_pago)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtBRL(t.saldo)}</td>
                <td className="px-4 py-2 text-right font-mono text-danger">{t.vencidas > 0 ? fmtBRL(t.vencido_valor) : "—"}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      {/* Drill-down modal */}
      {drill && (
        <CategoryDetailModal
          categoria={drill}
          kind={kind}
          from={from}
          to={to}
          onlyOpen={onlyOpen}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

function CategoryDetailModal({ categoria, kind, from, to, onlyOpen, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true); setErr("");
    api.get("/reports/finance/by-category/detail", { params: { categoria, kind, from_: from, to, only_open: onlyOpen } })
      .then((r) => setD(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [categoria, kind, from, to, onlyOpen]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="bycat-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-500 font-bold">
              {kind === "payable" ? "Contas a Pagar" : "Contas a Receber"} · Categoria
            </div>
            <div className="font-heading text-lg font-bold text-ink-900 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: CAT_FIN_COLOR[categoria] || "#78716C" }} />
              {fmtCatLabel(categoria)}
            </div>
            {d && (
              <div className="text-xs text-ink-500 mt-0.5">
                {d.n_contas} conta(s) · Total {fmtBRL(d.total)} · Saldo em aberto {fmtBRL(d.saldo)}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100 text-ink-500" data-testid="bycat-detail-close">✕</button>
        </div>

        {loading && <div className="p-8 flex justify-center"><Spinner /></div>}
        {err && <div className="p-4 text-sm text-red-700 bg-red-50">{err}</div>}
        {d && (
          <div className="p-6">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-3 py-2">Descrição</th>
                  <th className="px-3 py-2">{kind === "payable" ? "Fornecedor" : "Cliente"}</th>
                  <th className="px-3 py-2">Lançamento</th>
                  <th className="px-3 py-2">Vencimento</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-right">Pago</th>
                  <th className="px-3 py-2 text-right">Saldo</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((a) => (
                  <tr key={a.id} className={`border-b border-ink-100 last:border-0 hover:bg-stone-50 ${a.overdue && a.status !== "paga" ? "bg-red-50/40" : ""}`}>
                    <td className="px-3 py-1.5 font-medium text-ink-900 text-[12px] max-w-[240px] truncate" title={a.descricao}>{a.descricao}</td>
                    <td className="px-3 py-1.5 text-[12px] text-ink-700 max-w-[160px] truncate">{a.counterpart || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-ink-500">{a.created_at ? fmtDateBR(a.created_at) : "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-[12px]">{fmtDateBR(a.data_vencimento)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px]">{fmtBRL(a.valor)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px] text-emerald-700">{fmtBRL(a.total_pago)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[12px] font-semibold">{fmtBRL(a.saldo)}</td>
                    <td className="px-3 py-1.5 text-[11px]">
                      <span className={`inline-block px-2 py-0.5 rounded font-semibold uppercase ${
                        a.status === "paga" ? "bg-emerald-100 text-emerald-800" :
                        a.overdue ? "bg-red-100 text-red-800" :
                        a.status === "parcial" ? "bg-amber-100 text-amber-800" :
                        "bg-stone-100 text-ink-700"
                      }`}>{a.overdue && a.status !== "paga" ? "vencida" : a.status}</span>
                    </td>
                  </tr>
                ))}
                {d.rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500">Nenhuma conta encontrada.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

